import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { buildEtsyVariationMappingKey, buildEtsyVariationNameKey } from '@tulasihome/shared';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Eye,
  ImagePlus,
  Images,
  Loader2,
  RefreshCw,
  Search,
  Sparkles,
  Store,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { logging } from '@/services/logging';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getShippingCities, type ShippingAddressSuggestion } from '@/services/shippingService';
import { ImageZoomModal } from '@/components/order/ImageZoomModal';
import { formatPpi } from '@/components/order/utils/printSizeForm';
import {
  classifyAllEtsyVariationMappings,
  confirmEtsyVariationMappingClassification,
  confirmEtsyImportedOrderAddress,
  classifySingleEtsyVariationMapping,
  confirmEtsyImportedItemPaper,
  fetchEtsyPaperMatchingRules,
  fetchEtsyImportedItemImage,
  fetchEtsyUploadedImageCandidates,
  fetchEtsyVariationMappings,
  fetchPendingEtsyOrders,
  parseEtsyImportedOrderRawShipmentAddress,
  saveEtsyImportedOrderAddress,
  saveEtsyImportedItemPaper,
  saveEtsyImportedItemSize,
  uploadEtsyImportedItemFullSizeImage,
  assignImportedItemFullSizeImage,
  type EtsyPaperMatchingRule,
  type EtsyPendingOrder,
  type EtsyUploadedImageCandidatePage,
  type EtsyVariationMapping,
  updateEtsyVariationMappingClassification,
} from '@/services/etsyService';
import { getSignedUrlForFile } from '@/services/uploadService';
import { getPaperTypes, type PaperType } from '@/services/productService';
import EtsyWorkspaceNav from '@/components/etsy/EtsyWorkspaceNav';
import { EtsyOrderPreviewThumbnail } from '@/components/etsy/EtsyOrdersCanvas';
import { EtsyMassMatchTab } from '@/components/etsy/EtsyMassMatchTab';
import { ETSY_MATCHING_TAB_QUERY_KEY, getMatchingTab, MATCHING_TABS } from '@/components/etsy/etsyWorkspaceFlow';
import { getPreferredEtsyItemPreviewUrl } from '@/components/etsy/etsyPreview';
import { WORKSPACE_CANVAS_MODES } from '@/components/workspace/workspaceMode';
import type { ShippingAddress, ShippingAddressValidationErrors } from '@/lib/shippingAddress';
import { validateShippingAddress } from '@/lib/shippingAddress';
import { hasNovapostPresence } from '@/utils/novapost';
import noImageWatercolor from '@/assets/images/no-image-watercolor.svg';

type EtsyItemMatchRow = {
  readonly itemId: string;
  readonly transactionId: string;
  readonly etsyReceiptId: string;
  readonly shopName: string;
  readonly title: string;
  readonly quantity: number;
  readonly sizeLabel: string | null;
  readonly primaryPaperSignal: EtsyVariationMapping | null;
  readonly primaryPaperValueMapping: EtsyVariationMapping['valueMappings'][number] | null;
  readonly primarySizeSignal: EtsyVariationMapping | null;
  readonly primarySizeValueMapping: EtsyVariationMapping['valueMappings'][number] | null;
  readonly matchedPaperType: EtsyPendingOrder['items'][number]['matchedPaperType'];
  readonly paperAssignmentSource: EtsyPendingOrder['items'][number]['paperAssignmentSource'];
  readonly paperReviewStatus: EtsyPendingOrder['items'][number]['paperReviewStatus'];
  readonly paperAssignedAt: string | null;
  readonly matchedWidthIn: number | null;
  readonly matchedHeightIn: number | null;
  readonly sizeAssignmentSource: EtsyPendingOrder['items'][number]['sizeAssignmentSource'];
  readonly sizeAssignedAt: string | null;
  readonly previewUrl: string | null;
  readonly fullSizePreviewUrl: string | null;
};

type EtsyAddressDraft = ShippingAddress;
type EtsyAddressDraftErrors = ShippingAddressValidationErrors;
type MatchingTagTone = 'neutral' | 'success' | 'info' | 'warning';

const getMatchingTagClassName = (tone: MatchingTagTone = 'neutral') => {
  const baseClassName = 'rounded-[6px] border px-2 py-0.5 text-[11px] font-medium shadow-none';

  switch (tone) {
    case 'success':
      return `${baseClassName} border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200`;
    case 'info':
      return `${baseClassName} border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-50 dark:border-sky-900/60 dark:bg-sky-950/30 dark:text-sky-200`;
    case 'warning':
      return `${baseClassName} border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200`;
    case 'neutral':
    default:
      return `${baseClassName} border-border bg-muted/35 text-muted-foreground hover:bg-muted/35`;
  }
};

const formatDateTime = (value: string | null | undefined) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatFileSize = (value: number | null | undefined) => {
  if (!value || value <= 0) {
    return null;
  }

  if (value >= 1024 * 1024) {
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (value >= 1024) {
    return `${(value / 1024).toFixed(1)} KB`;
  }

  return `${value} B`;
};

const formatImageDimensions = (
  file: EtsyPendingOrder['items'][number]['fullSizeUploadedFile'] | null | undefined,
) => {
  if (!file?.originalWidthPx || !file.originalHeightPx) {
    return null;
  }

  const dpi = formatPpi(
    typeof file.dpiX === 'number' ? Math.round(file.dpiX) : null,
    typeof file.dpiY === 'number' ? Math.round(file.dpiY) : null,
  );

  return dpi !== '—'
    ? `${file.originalWidthPx} x ${file.originalHeightPx}px, ${dpi}`
    : `${file.originalWidthPx} x ${file.originalHeightPx}px`;
};

const TARGET_IMAGE_PPI = 300;
const UPLOADED_IMAGE_CANDIDATE_PAGE_SIZE = 12;

const formatPrintSizeIn = (value: number): string => (
  value >= 10 ? value.toFixed(1) : value.toFixed(2)
);

const formatExpectedPrintSize = (
  widthIn: number | null | undefined,
  heightIn: number | null | undefined,
) => (
  widthIn && heightIn ? `${formatPrintSizeIn(widthIn)} x ${formatPrintSizeIn(heightIn)} in` : null
);

const formatPercent = (value: number | null | undefined) => (
  typeof value === 'number' && Number.isFinite(value) ? `${Math.round(value * 100)}%` : null
);

const formatRealPrintSizeAt300Ppi = (
  file: EtsyPendingOrder['items'][number]['fullSizeUploadedFile'] | null | undefined,
) => {
  if (!file?.originalWidthPx || !file.originalHeightPx) {
    return null;
  }

  return `${formatPrintSizeIn(file.originalWidthPx / TARGET_IMAGE_PPI)} x ${formatPrintSizeIn(file.originalHeightPx / TARGET_IMAGE_PPI)} in`;
};

const resolvePreviewPrintDimensionIn = (
  matchedValueIn: number | null | undefined,
  originalPixels: number | null | undefined,
) => {
  if (typeof matchedValueIn === 'number' && matchedValueIn > 0) {
    return matchedValueIn;
  }

  if (typeof originalPixels === 'number' && originalPixels > 0) {
    return originalPixels / TARGET_IMAGE_PPI;
  }

  return null;
};

const getPaperLabel = (
  paper: { readonly brand: string; readonly name: string } | null | undefined,
  t?: TFunction,
) => {
  if (!paper) {
    return t ? t('integrations.etsy.common.notMatchedYet', 'Not matched yet') : '';
  }
  return `${paper.brand} - ${paper.name}`;
};

const getVariationDisplayState = (
  mapping: EtsyVariationMapping,
  t: TFunction,
) => {
  const isUnclassified = mapping.classificationSource == null;

  if (isUnclassified) {
    return {
      isUnclassified: true,
      label: t('integrations.etsy.matching.variation.needsSelection', 'Needs selection'),
      description: t('integrations.etsy.matching.variation.needsSelectionDescription', 'Choose Paper, Size, or Ignore. Ask AI can suggest it first.'),
    };
  }

  if (mapping.classification === 'UNKNOWN') {
    return {
      isUnclassified: false,
      label: t('integrations.etsy.matching.variation.ignore', 'Ignore'),
      description: t('integrations.etsy.matching.variation.ignoreDescription', 'This variation key is ignored by downstream matching.'),
    };
  }

  return {
    isUnclassified: false,
    label: mapping.classification,
    description: mapping.classification === 'PAPER'
      ? t('integrations.etsy.matching.variation.paperDescription', 'Papers tab will use values under this key.')
      : t('integrations.etsy.matching.variation.sizeDescription', 'Sizes tab will use values under this key.'),
  };
};

const getVariationClassificationTagTone = (mapping: EtsyVariationMapping): MatchingTagTone => {
  if (mapping.classificationSource == null) {
    return 'warning';
  }

  if (mapping.classification === 'PAPER') {
    return 'info';
  }

  if (mapping.classification === 'SIZE') {
    return 'success';
  }

  return 'neutral';
};

const getAssignmentSourceTagTone = (source: string | null | undefined): MatchingTagTone => {
  if (source === 'AI' || source === 'REUSED') {
    return 'info';
  }

  if (source === 'MANUAL') {
    return 'neutral';
  }

  return 'neutral';
};

const isVariationMappingReviewPending = (mapping: EtsyVariationMapping) => {
  if (mapping.classificationSource == null || mapping.reviewStatus === 'PENDING') {
    return true;
  }

  if (mapping.classificationSource !== 'AI') {
    return false;
  }

  if (!mapping.lastReviewedAt) {
    return true;
  }

  if (!mapping.lastClassifiedAt) {
    return false;
  }

  return new Date(mapping.lastReviewedAt).getTime() <= new Date(mapping.lastClassifiedAt).getTime();
};

const isVariationMappingReviewedForDownstream = (mapping: EtsyVariationMapping) =>
  !isVariationMappingReviewPending(mapping) && mapping.reviewStatus !== 'PENDING';

const compareReceiptTitleTransaction = (
  left: { etsyReceiptId: string; title: string; transactionId: string },
  right: { etsyReceiptId: string; title: string; transactionId: string },
) => {
  const receiptCompare = right.etsyReceiptId.localeCompare(left.etsyReceiptId, undefined, { numeric: true });
  if (receiptCompare !== 0) {
    return receiptCompare;
  }

  const titleCompare = left.title.localeCompare(right.title, undefined, { sensitivity: 'base' });
  if (titleCompare !== 0) {
    return titleCompare;
  }

  return left.transactionId.localeCompare(right.transactionId, undefined, { numeric: true });
};

const getPaperRowPriority = (row: EtsyItemMatchRow) => {
  if (!row.matchedPaperType) {
    return 0;
  }
  if (row.paperReviewStatus !== 'CONFIRMED') {
    return 1;
  }
  return 2;
};

const getSizeRowPriority = (row: EtsyItemMatchRow) => (
  row.matchedWidthIn == null || row.matchedHeightIn == null ? 0 : 1
);

const getAddressRowPriority = (order: EtsyPendingOrder) => (
  order.addressReviewStatus === 'CONFIRMED' ? 1 : 0
);

const getImageRowPriority = (item: EtsyPendingOrder['items'][number]) => {
  if (item.fullSizeUploadedFile == null) return 0;
  if (item.fullSizeImageSource === 'REUSED') return 1;
  return 2;
};

const splitBuyerName = (value: string | null | undefined): { firstName: string; lastName: string } => {
  const normalized = value?.trim() ?? '';
  if (!normalized) {
    return { firstName: '', lastName: '' };
  }

  const parts = normalized.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }

  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts[parts.length - 1] ?? '',
  };
};

const buildAddressDraftFromOrder = (order: EtsyPendingOrder): EtsyAddressDraft => {
  if (order.matchedAddress) {
    return {
      countryCode: order.matchedAddress.countryCode,
      firstName: order.matchedAddress.firstName,
      lastName: order.matchedAddress.lastName,
      phone: order.matchedAddress.phone,
      street1: order.matchedAddress.street1,
      street2: order.matchedAddress.street2 ?? '',
      city: order.matchedAddress.city,
      novaposhtaCityRef: order.matchedAddress.novaposhtaCityRef ?? undefined,
      region: order.matchedAddress.region,
      postalCode: order.matchedAddress.postalCode,
      setAsDefault: false,
    };
  }

  const name = splitBuyerName(order.etsyData.name);
  return {
    countryCode: (order.etsyData.country_iso ?? '').trim().toUpperCase(),
    firstName: name.firstName,
    lastName: name.lastName,
    phone: '',
    street1: order.etsyData.first_line?.trim() ?? '',
    street2: order.etsyData.second_line?.trim() ?? '',
    city: order.etsyData.city?.trim() ?? '',
    region: order.etsyData.state?.trim() ?? '',
    postalCode: order.etsyData.zip?.trim() ?? '',
    setAsDefault: false,
  };
};

const validateAddressDraft = (
  draft: EtsyAddressDraft,
  t: TFunction,
): EtsyAddressDraftErrors => validateShippingAddress(
  draft,
  {
    countryCode: t('integrations.etsy.matching.addressErrors.countryCode', 'Country code is required or invalid.'),
    firstName: t('integrations.etsy.matching.addressErrors.firstName', 'First name is required.'),
    lastName: t('integrations.etsy.matching.addressErrors.lastName', 'Last name is required.'),
    phone: t('integrations.etsy.matching.addressErrors.phone', 'Phone should be at least 7 characters.'),
    street1: t('integrations.etsy.matching.addressErrors.street1', 'Street should be at least 5 characters.'),
    poBoxStreet: t('integrations.etsy.matching.addressErrors.poBoxStreet', 'PO Box is allowed, but please also add the full street and house number.'),
    city: t('integrations.etsy.matching.addressErrors.city', 'City should be at least 2 characters.'),
    novaposhtaCitySelection: t('integrations.etsy.matching.addressErrors.novaposhtaCitySelection', 'Select a city from Nova Post suggestions.'),
    region: t('integrations.etsy.matching.addressErrors.region', 'Region should be at least 2 characters.'),
    postalCode: t('integrations.etsy.matching.addressErrors.postalCode', 'Postal code should be at least 3 characters.'),
  },
  {
    requiresNovaposhtaCitySelection: hasNovapostPresence(draft.countryCode),
  },
);

const hasAddressDraftErrors = (errors: EtsyAddressDraftErrors): boolean => Object.keys(errors).length > 0;

const getAddressFieldClassName = (hasError: boolean) => (
  hasError ? 'border-destructive focus-visible:ring-destructive/40' : undefined
);

const isDraftEqualToMatchedAddress = (order: EtsyPendingOrder, draft: EtsyAddressDraft): boolean => {
  const matched = order.matchedAddress;
  if (!matched) {
    return false;
  }

  return matched.countryCode === draft.countryCode.trim().toUpperCase()
    && matched.firstName === draft.firstName.trim()
    && matched.lastName === draft.lastName.trim()
    && matched.phone === draft.phone.trim()
    && matched.street1 === draft.street1.trim()
    && (matched.street2 ?? '') === (draft.street2?.trim() ?? '')
    && matched.city === draft.city.trim()
    && (matched.novaposhtaCityRef ?? '') === (draft.novaposhtaCityRef?.trim() ?? '')
    && matched.region === draft.region.trim()
    && matched.postalCode === draft.postalCode.trim();
};

const formatAddressBlock = (...parts: Array<string | null | undefined>) =>
  parts
    .map((part) => part?.trim() ?? '')
    .filter(Boolean)
    .join(', ');

const hasEtsyShipmentAddress = (order: EtsyPendingOrder): boolean => Boolean(
  order.etsyData.formatted_address?.trim()
  || order.etsyData.first_line?.trim()
  || order.etsyData.second_line?.trim()
  || order.etsyData.city?.trim()
  || order.etsyData.state?.trim()
  || order.etsyData.zip?.trim()
  || order.etsyData.country_iso?.trim()
);

const normalizeSuggestionName = (value: string): string => value
  .trim()
  .toLowerCase()
  .replace(/\s+/g, ' ');

const getSizeSignalLabel = (item: EtsyPendingOrder['items'][number]): string | null => {
  if (item.sizeSelection?.trim()) {
    return item.sizeSelection.trim();
  }

  const sizeEntry = item.variationSummary.find((entry) => {
    const name = entry.name.toLowerCase();
    return name.includes('size') || name.includes('dimension') || name.includes('format');
  });

  return sizeEntry?.value.trim() || null;
};

const EtsyMatchingCanvas = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState<ReadonlyArray<EtsyPendingOrder>>([]);
  const [variationMappings, setVariationMappings] = useState<ReadonlyArray<EtsyVariationMapping>>([]);
  const [paperRules, setPaperRules] = useState<ReadonlyArray<EtsyPaperMatchingRule>>([]);
  const [paperTypes, setPaperTypes] = useState<ReadonlyArray<PaperType>>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [isMassMatching, setIsMassMatching] = useState(false);
  const [selectedPaperByItemId, setSelectedPaperByItemId] = useState<Record<string, string>>({});
  const [savingItemId, setSavingItemId] = useState<string | null>(null);
  const [confirmingItemId, setConfirmingItemId] = useState<string | null>(null);
  const [savingSizeItemId, setSavingSizeItemId] = useState<string | null>(null);
  const [savingAddressOrderId, setSavingAddressOrderId] = useState<string | null>(null);
  const [confirmingAddressOrderId, setConfirmingAddressOrderId] = useState<string | null>(null);
  const [validatingAddressCityOrderId, setValidatingAddressCityOrderId] = useState<string | null>(null);
  const [loadingAddressCitySuggestionsOrderId, setLoadingAddressCitySuggestionsOrderId] = useState<string | null>(null);
  const [addressCitySuggestionsOpenOrderId, setAddressCitySuggestionsOpenOrderId] = useState<string | null>(null);
  const [addressCitySuggestionsByOrderId, setAddressCitySuggestionsByOrderId] = useState<Record<string, ReadonlyArray<ShippingAddressSuggestion>>>({});
  const [editingSizeItemId, setEditingSizeItemId] = useState<string | null>(null);
  const [classifyingAll, setClassifyingAll] = useState(false);
  const [classifyingMappingId, setClassifyingMappingId] = useState<string | null>(null);
  const [savingVariationTypeId, setSavingVariationTypeId] = useState<string | null>(null);
  const [confirmingVariationTypeId, setConfirmingVariationTypeId] = useState<string | null>(null);
  const [draftSizeByItemId, setDraftSizeByItemId] = useState<Record<string, { widthIn: string; heightIn: string }>>({});
  const [addressDraftByOrderId, setAddressDraftByOrderId] = useState<Record<string, EtsyAddressDraft>>({});
  const [rawShipmentAddressByOrderId, setRawShipmentAddressByOrderId] = useState<Record<string, string>>({});
  const [parsingRawAddressOrderId, setParsingRawAddressOrderId] = useState<string | null>(null);
  const [uploadingImageItemId, setUploadingImageItemId] = useState<string | null>(null);
  const [uploadedImageSelectorItem, setUploadedImageSelectorItem] = useState<EtsyPendingOrder['items'][number] | null>(null);
  const [uploadedImageCandidates, setUploadedImageCandidates] = useState<EtsyUploadedImageCandidatePage | null>(null);
  const [uploadedImageCandidatesStatus, setUploadedImageCandidatesStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [uploadedImageCandidatesError, setUploadedImageCandidatesError] = useState<string | null>(null);
  const [showAllUploadedImages, setShowAllUploadedImages] = useState(false);
  const [uploadedImageCandidatesPage, setUploadedImageCandidatesPage] = useState(1);
  const [assigningUploadedImageId, setAssigningUploadedImageId] = useState<string | null>(null);
  const [fetchingDraftImageItemId, setFetchingDraftImageItemId] = useState<string | null>(null);
  const [queuedDraftImageItemIds, setQueuedDraftImageItemIds] = useState<ReadonlyArray<string>>([]);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewMeta, setPreviewMeta] = useState<{
    widthIn: number | null;
    heightIn: number | null;
    marginIn: number | null;
    originalWidthPx: number | null;
    originalHeightPx: number | null;
  } | null>(null);
  const imageInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const rawAddressParseTimersRef = useRef<Record<string, ReturnType<typeof window.setTimeout> | undefined>>({});
  const autoClassifiedVariationKeyRef = useRef<string | null>(null);
  const autoAttemptedDraftImageItemIdsRef = useRef<Set<string>>(new Set());
  const draftImageBatchRef = useRef<{
    active: boolean;
    total: number;
    completed: number;
  }>({
    active: false,
    total: 0,
    completed: 0,
  });

  const activeTab = getMatchingTab(searchParams.get(ETSY_MATCHING_TAB_QUERY_KEY));

  useEffect(() => {
    setIsMassMatching(false);
  }, [activeTab]);

  const loadData = useCallback(async (options?: { readonly silent?: boolean }) => {
    try {
      if (!options?.silent) {
        setStatus('loading');
        setError(null);
      }
      const [nextOrders, nextMappings, nextRules, nextPaperTypes] = await Promise.all([
        fetchPendingEtsyOrders(),
        fetchEtsyVariationMappings(),
        fetchEtsyPaperMatchingRules(),
        getPaperTypes(),
      ]);

      setOrders(nextOrders);
      setVariationMappings(nextMappings);
      setPaperRules(nextRules);
      setPaperTypes(nextPaperTypes.filter((paper) => paper.available !== false));
      if (!options?.silent) {
        setStatus('idle');
      }
    } catch (loadError) {
      logging.error('Failed to load Etsy matching workspace', { error: loadError });
      const message = loadError instanceof Error ? loadError.message : 'Failed to load Etsy matching workspace.';
      setError(message);
      setStatus('error');
      toast({
        title: t('integrations.etsy.toasts.matchingLoadFailedTitle', 'Failed to load Etsy matching'),
        description: message,
        variant: 'destructive',
      });
    }
  }, [toast]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    setAddressDraftByOrderId((current) => {
      const next = { ...current };
      for (const order of orders) {
        if (!next[order.id]) {
          next[order.id] = buildAddressDraftFromOrder(order);
        }
      }

      for (const orderId of Object.keys(next)) {
        if (!orders.some((order) => order.id === orderId)) {
          delete next[orderId];
        }
      }

      return next;
    });

    setRawShipmentAddressByOrderId((current) => {
      const next = { ...current };
      for (const order of orders) {
        if (next[order.id] == null) {
          next[order.id] = order.rawShipmentAddressText ?? '';
        }
      }

      for (const orderId of Object.keys(next)) {
        if (!orders.some((order) => order.id === orderId)) {
          delete next[orderId];
        }
      }

      return next;
    });
  }, [orders]);

  useEffect(() => () => {
    for (const timer of Object.values(rawAddressParseTimersRef.current)) {
      if (timer) {
        window.clearTimeout(timer);
      }
    }
  }, []);

  const variationMappingsByNameKey = useMemo(() => {
    return new Map(variationMappings.map((mapping) => [mapping.normalizedKey, mapping]));
  }, [variationMappings]);

  const itemRows = useMemo<ReadonlyArray<EtsyItemMatchRow>>(() => {
    const inboxOrders = orders.filter((order) => order.selectedForInbox);
    return inboxOrders.flatMap((order) => order.items.map((item) => {
      const matchingSignals = item.variationSummary
        .map((variation) => variationMappingsByNameKey.get(buildEtsyVariationNameKey(variation.name)) ?? null)
        .filter((variation): variation is EtsyVariationMapping => variation != null && isVariationMappingReviewedForDownstream(variation));

      const primaryPaperSignal = matchingSignals.find((signal) => signal.classification === 'PAPER') ?? null;
      const primaryPaperVariation = primaryPaperSignal
        ? item.variationSummary.find((variation) => buildEtsyVariationNameKey(variation.name) === primaryPaperSignal.normalizedKey) ?? null
        : null;
      const primaryPaperValueMapping = primaryPaperSignal && primaryPaperVariation
        ? primaryPaperSignal.valueMappings.find((entry) => entry.normalizedKey === buildEtsyVariationMappingKey(primaryPaperVariation.name, primaryPaperVariation.value)) ?? null
        : null;

      const primarySizeSignal = matchingSignals.find((signal) => signal.classification === 'SIZE') ?? null;
      const primarySizeVariation = primarySizeSignal
        ? item.variationSummary.find((variation) => buildEtsyVariationNameKey(variation.name) === primarySizeSignal.normalizedKey) ?? null
        : null;
      const primarySizeValueMapping = primarySizeSignal && primarySizeVariation
        ? primarySizeSignal.valueMappings.find((entry) => entry.normalizedKey === buildEtsyVariationMappingKey(primarySizeVariation.name, primarySizeVariation.value)) ?? null
        : null;

      return {
        itemId: item.id,
        transactionId: item.transactionId,
        etsyReceiptId: order.etsyReceiptId,
        shopName: order.shopName,
        title: item.title,
        quantity: item.quantity,
        sizeLabel: getSizeSignalLabel(item),
        primaryPaperSignal,
        primaryPaperValueMapping,
        primarySizeSignal,
        primarySizeValueMapping,
        matchedPaperType: item.matchedPaperType,
        paperAssignmentSource: item.paperAssignmentSource,
        paperReviewStatus: item.paperReviewStatus,
        paperAssignedAt: item.paperAssignedAt,
        matchedWidthIn: item.matchedWidthIn,
        matchedHeightIn: item.matchedHeightIn,
        sizeAssignmentSource: item.sizeAssignmentSource,
        sizeAssignedAt: item.sizeAssignedAt,
        previewUrl: item.previewUrl,
        fullSizePreviewUrl: item.fullSizePreviewUrl,
      };
    }));
  }, [orders, variationMappingsByNameKey]);

  const filteredVariationMappings = useMemo(() => {
    const term = query.trim().toLowerCase();
    const inboxOrders = orders.filter((order) => order.selectedForInbox);
    
    // Get all unique normalized keys currently present in the inbox items
    const inboxVariationKeys = new Set(
      inboxOrders.flatMap(order => 
        order.items.flatMap(item => 
          item.variationSummary.map(v => buildEtsyVariationNameKey(v.name))
        )
      )
    );
    
    // Only show mappings that are actually used in the current inbox
    const relevantMappings = variationMappings.filter((mapping) => 
      inboxVariationKeys.has(mapping.normalizedKey)
    );

    const filtered = !term ? relevantMappings : relevantMappings.filter((mapping) => [
      mapping.etsyShop.shopName,
      mapping.variationName,
      ...mapping.sampleValues,
      mapping.classification,
      mapping.classificationSource,
      mapping.reviewStatus,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(term));

    return filtered
      .slice()
      .sort((left, right) => {
        const leftPriority = left.classificationSource == null ? 0 : 1;
        const rightPriority = right.classificationSource == null ? 0 : 1;
        if (leftPriority !== rightPriority) {
          return leftPriority - rightPriority;
        }

        const leftReviewPriority = isVariationMappingReviewPending(left) ? 0 : 1;
        const rightReviewPriority = isVariationMappingReviewPending(right) ? 0 : 1;
        if (leftReviewPriority !== rightReviewPriority) {
          return leftReviewPriority - rightReviewPriority;
        }

        const nameCompare = left.variationName.localeCompare(right.variationName, undefined, { sensitivity: 'base' });
        if (nameCompare !== 0) {
          return nameCompare;
        }

        const shopCompare = left.etsyShop.shopName.localeCompare(right.etsyShop.shopName, undefined, { sensitivity: 'base' });
        if (shopCompare !== 0) {
          return shopCompare;
        }

        return left.id.localeCompare(right.id);
      });
  }, [query, variationMappings]);

  const filteredPaperRows = useMemo(() => {
    const term = query.trim().toLowerCase();
    const filtered = !term ? itemRows : itemRows.filter((row) => [
      row.primaryPaperSignal?.variationName,
      ...row.primaryPaperSignal?.sampleValues ?? [],
      row.primaryPaperValueMapping?.paperType?.name,
      row.primaryPaperValueMapping?.paperType?.brand,
      row.shopName,
      row.title,
      row.etsyReceiptId,
      row.transactionId,
      row.sizeLabel,
      row.matchedPaperType?.name,
      row.matchedPaperType?.brand,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(term));

    return filtered
      .slice()
      .sort((left, right) => {
        const priorityCompare = getPaperRowPriority(left) - getPaperRowPriority(right);
        if (priorityCompare !== 0) {
          return priorityCompare;
        }

        return compareReceiptTitleTransaction(left, right);
      });
  }, [itemRows, query]);

  const groupedPaperRows = useMemo(() => {
    const groups = new Map<string, {
      readonly key: string;
      readonly label: string;
      readonly shopName: string;
      readonly signal: EtsyVariationMapping | null;
      items: EtsyItemMatchRow[];
    }>();

    for (const row of filteredPaperRows) {
      const groupKey = row.primaryPaperSignal
        ? `${row.primaryPaperSignal.etsyShopId}::${row.primaryPaperSignal.normalizedKey}::${row.primaryPaperValueMapping?.variationValue ?? 'unresolved'}`
        : `unclassified::${row.itemId}`;
      const label = row.primaryPaperSignal
        ? `${row.primaryPaperSignal.variationName}: ${row.primaryPaperValueMapping?.variationValue ?? 'Unknown value'}`
        : 'Needs variation review before paper matching';
      const existingGroup = groups.get(groupKey);
      if (existingGroup) {
        existingGroup.items.push(row);
      } else {
        groups.set(groupKey, {
          key: groupKey,
          label,
          shopName: row.shopName,
          signal: row.primaryPaperSignal,
          items: [row],
        });
      }
    }

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        items: group.items.slice().sort((left, right) => {
          const priorityCompare = getPaperRowPriority(left) - getPaperRowPriority(right);
          if (priorityCompare !== 0) {
            return priorityCompare;
          }

          return compareReceiptTitleTransaction(left, right);
        }),
      }))
      .sort((left, right) => {
        const leftFirst = left.items[0];
        const rightFirst = right.items[0];
        if (leftFirst && rightFirst) {
          const priorityCompare = getPaperRowPriority(leftFirst) - getPaperRowPriority(rightFirst);
          if (priorityCompare !== 0) {
            return priorityCompare;
          }

          const itemCompare = compareReceiptTitleTransaction(leftFirst, rightFirst);
          if (itemCompare !== 0) {
            return itemCompare;
          }
        }

        return left.label.localeCompare(right.label, undefined, { sensitivity: 'base' });
      });
  }, [filteredPaperRows]);

  const filteredAddressRows = useMemo(() => {
    const term = query.trim().toLowerCase();
    const inboxOrders = orders.filter((order) => order.selectedForInbox);
    const filtered = !term ? inboxOrders : inboxOrders.filter((order) => [
      order.shopName,
      order.etsyReceiptId,
      order.etsyData.name,
      order.etsyData.buyer_email,
      order.etsyData.first_line,
      order.etsyData.second_line,
      order.etsyData.city,
      order.etsyData.state,
      order.etsyData.zip,
      order.etsyData.country_iso,
      order.etsyData.formatted_address,
      order.rawShipmentAddressText,
      order.matchedAddress?.firstName,
      order.matchedAddress?.lastName,
      order.matchedAddress?.phone,
      order.matchedAddress?.street1,
      order.matchedAddress?.street2,
      order.matchedAddress?.city,
      order.matchedAddress?.region,
      order.matchedAddress?.postalCode,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(term));

    return filtered
      .slice()
      .sort((left, right) => {
        const priorityCompare = getAddressRowPriority(left) - getAddressRowPriority(right);
        if (priorityCompare !== 0) {
          return priorityCompare;
        }

        return right.etsyReceiptId.localeCompare(left.etsyReceiptId, undefined, { numeric: true });
      });
  }, [orders, query]);

  const filteredSizeRows = useMemo(() => {
    const term = query.trim().toLowerCase();
    const filtered = itemRows
      .filter((row) => row.primarySizeSignal || (row.sizeLabel && !row.primaryPaperSignal))
      .filter((row) => !term || [
      row.shopName,
      row.title,
      row.etsyReceiptId,
      row.sizeLabel,
      row.primarySizeSignal?.variationName,
      ...row.primarySizeSignal?.sampleValues ?? [],
      row.primarySizeValueMapping?.normalizedSizeLabel,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(term));

    return filtered
      .slice()
      .sort((left, right) => {
        const priorityCompare = getSizeRowPriority(left) - getSizeRowPriority(right);
        if (priorityCompare !== 0) {
          return priorityCompare;
        }

        return compareReceiptTitleTransaction(left, right);
      });
  }, [itemRows, query]);

  const filteredImageRows = useMemo(() => {
    const term = query.trim().toLowerCase();
    const inboxOrders = orders.filter((order) => order.selectedForInbox);
    const filtered = inboxOrders
      .flatMap((order) => order.items.map((item) => ({
        order,
        item,
      })))
      .filter(({ order, item }) => !term || [
      order.shopName,
      order.etsyReceiptId,
      item.transactionId,
      item.title,
      item.sku,
      item.listingImageId,
      item.fullSizeUploadedFile?.filename,
      item.imageAvailability.note,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(term));

    return filtered
      .slice()
      .sort((left, right) => {
        const priorityCompare = getImageRowPriority(left.item) - getImageRowPriority(right.item);
        if (priorityCompare !== 0) {
          return priorityCompare;
        }

        return compareReceiptTitleTransaction({
          etsyReceiptId: left.order.etsyReceiptId,
          title: left.item.title,
          transactionId: left.item.transactionId,
        }, {
          etsyReceiptId: right.order.etsyReceiptId,
          title: right.item.title,
          transactionId: right.item.transactionId,
        });
      });
  }, [orders, query]);

  const metrics = useMemo(() => {
    const inboxOrders = orders.filter((order) => order.selectedForInbox);
    const totalItems = inboxOrders.reduce((sum, order) => sum + order.items.length, 0);
    
    // Scoped variation metrics
    const inboxVariationKeys = new Set(
      inboxOrders.flatMap(order => 
        order.items.flatMap(item => 
          item.variationSummary.map(v => buildEtsyVariationNameKey(v.name))
        )
      )
    );
    const relevantMappings = variationMappings.filter((mapping) => 
      inboxVariationKeys.has(mapping.normalizedKey)
    );
    const variationPending = relevantMappings.filter(isVariationMappingReviewPending).length;

    const pendingSizeMappings = itemRows.filter((row) => (
      (row.primarySizeSignal != null || row.sizeLabel != null)
      && (row.matchedWidthIn == null || row.matchedHeightIn == null)
    )).length;
    const pendingPaperMappings = itemRows.filter((row) => row.matchedPaperType == null).length;
    const pendingAddressMappings = inboxOrders.filter((order) => order.addressReviewStatus !== 'CONFIRMED').length;
    const pendingImageUploads = inboxOrders.reduce((sum, order) => (
      sum + order.items.filter((item) => item.fullSizeUploadedFile == null).length
    ), 0);

    return {
      totalOrders: inboxOrders.length,
      totalItems,
      variationPending,
      pendingSizeMappings,
      pendingPaperMappings,
      pendingAddressMappings,
      pendingImageUploads,
    };
  }, [itemRows, orders, variationMappings]);

  const handleClassifyAll = useCallback(async (options?: { readonly notifyStart?: boolean }): Promise<boolean> => {
    try {
      if (options?.notifyStart) {
        toast({
          title: t('integrations.etsy.toasts.classifyingVariationsTitle', 'Classifying variations'),
          description: t('integrations.etsy.toasts.classifyingVariationsDescription', 'TulasiHome started AI classification for unseen Etsy variation keys.'),
        });
      }
      setClassifyingAll(true);
      const results = await classifyAllEtsyVariationMappings();
      setVariationMappings((current) => {
        const byId = new Map(current.map((entry) => [entry.id, entry]));
        for (const entry of results) {
          byId.set(entry.id, entry);
        }
        return Array.from(byId.values());
      });
      await loadData({ silent: true });
      toast({
        title: t('integrations.etsy.toasts.variationMappingUpdatedTitle', 'Variation mapping updated'),
        description: t('integrations.etsy.toasts.variationMappingUpdatedDescription', '{{count}} Etsy variation signals were classified with AI.', { count: results.length }),
      });
      return true;
    } catch (classifyError) {
      logging.error('Failed to classify Etsy variation mappings', { error: classifyError });
      toast({
        title: t('integrations.etsy.toasts.classifyVariationsFailedTitle', 'Could not classify variation mappings'),
        description: classifyError instanceof Error ? classifyError.message : 'Failed to classify Etsy variation mappings.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setClassifyingAll(false);
    }
  }, [loadData, toast]);

  const pendingVariationKey = useMemo(() => variationMappings
    .filter((mapping) => mapping.classificationSource == null)
    .map((mapping) => mapping.id)
    .sort()
    .join(','), [variationMappings]);

  useEffect(() => {
    if (activeTab !== MATCHING_TABS.variations || status !== 'idle' || classifyingAll || !pendingVariationKey) {
      return;
    }

    if (autoClassifiedVariationKeyRef.current === pendingVariationKey) {
      return;
    }

    autoClassifiedVariationKeyRef.current = pendingVariationKey;

    void (async () => {
      const success = await handleClassifyAll({ notifyStart: true });
      if (!success) {
        autoClassifiedVariationKeyRef.current = null;
      }
    })();
  }, [activeTab, classifyingAll, handleClassifyAll, pendingVariationKey, status]);

  const handleClassifySingle = useCallback(async (mappingId: string) => {
    try {
      setClassifyingMappingId(mappingId);
      const updated = await classifySingleEtsyVariationMapping(mappingId);
      setVariationMappings((current) => current.map((entry) => entry.id === updated.id ? updated : entry));
      await loadData({ silent: true });
    } catch (classifyError) {
      logging.error('Failed to classify Etsy variation mapping', { error: classifyError, mappingId });
      toast({
        title: t('integrations.etsy.toasts.classifyVariationFailedTitle', 'Could not classify variation'),
        description: classifyError instanceof Error ? classifyError.message : 'Failed to classify Etsy variation mapping.',
        variant: 'destructive',
      });
    } finally {
      setClassifyingMappingId(null);
    }
  }, [loadData, toast]);

  const handleSetVariationType = useCallback(async (
    mappingId: string,
    classification: 'SIZE' | 'PAPER' | 'UNKNOWN',
  ) => {
    try {
      setSavingVariationTypeId(mappingId);
      const updated = await updateEtsyVariationMappingClassification(mappingId, classification);
      setVariationMappings((current) => current.map((entry) => {
        if (entry.etsyShopId === updated.etsyShopId && entry.normalizedKey === updated.normalizedKey) {
          return updated;
        }
        return entry.id === updated.id ? updated : entry;
      }));
      await loadData({ silent: true });
      toast({
        title: t('integrations.etsy.toasts.variationTypeUpdatedTitle', 'Variation type updated'),
        description: t('integrations.etsy.toasts.variationTypeUpdatedDescription', '{{name}} is now treated as {{classification}}.', { name: updated.variationName, classification }),
      });
    } catch (saveError) {
      logging.error('Failed to update Etsy variation type', { error: saveError, mappingId, classification });
      toast({
        title: t('integrations.etsy.toasts.variationTypeUpdateFailedTitle', 'Could not update variation type'),
        description: saveError instanceof Error ? saveError.message : 'Failed to update Etsy variation type.',
        variant: 'destructive',
      });
    } finally {
      setSavingVariationTypeId(null);
    }
  }, [loadData, toast]);

  const handleConfirmVariationType = useCallback(async (mappingId: string) => {
    try {
      setConfirmingVariationTypeId(mappingId);
      const updated = await confirmEtsyVariationMappingClassification(mappingId);
      setVariationMappings((current) => current.map((entry) => {
        if (entry.etsyShopId === updated.etsyShopId && entry.normalizedKey === updated.normalizedKey) {
          return updated;
        }
        return entry.id === updated.id ? updated : entry;
      }));
      await loadData({ silent: true });
      toast({
        title: t('integrations.etsy.toasts.variationTypeConfirmedTitle', 'Variation type confirmed'),
        description: t('integrations.etsy.toasts.variationTypeConfirmedDescription', '{{name}} is marked as reviewed.', { name: updated.variationName }),
      });
    } catch (confirmError) {
      logging.error('Failed to confirm Etsy variation type', { error: confirmError, mappingId });
      toast({
        title: t('integrations.etsy.toasts.variationTypeConfirmFailedTitle', 'Could not confirm variation type'),
        description: confirmError instanceof Error ? confirmError.message : 'Failed to confirm Etsy variation type.',
        variant: 'destructive',
      });
    } finally {
      setConfirmingVariationTypeId(null);
    }
  }, [loadData, toast]);

  const handleSaveItemPaper = useCallback(async (row: EtsyItemMatchRow, paperTypeId: string) => {
    if (!paperTypeId) {
      toast({
        title: t('integrations.etsy.toasts.selectPaperFirstTitle', 'Select a paper first'),
        description: t('integrations.etsy.toasts.selectPaperFirstDescription', 'Choose a production paper before applying it to this Etsy item.'),
        variant: 'destructive',
      });
      return;
    }

    const savedRule = row.primaryPaperSignal
      ? paperRules.find((rule) => rule.etsyShopId === row.primaryPaperSignal?.etsyShopId && rule.sourceKey === row.primaryPaperSignal?.normalizedKey)
      : null;

    const shouldReplaceRule = Boolean(
      row.primaryPaperSignal
      && savedRule
      && savedRule.paperType?.id !== paperTypeId
        && window.confirm(t('integrations.etsy.papers.replaceRuleConfirm', 'Apply this paper to all newly matched similar items and replace the saved Etsy paper rule for future matching?')),
    );

    try {
      setSavingItemId(row.itemId);
      const result = await saveEtsyImportedItemPaper(row.itemId, {
        paperTypeId,
        replaceRuleForFuture: shouldReplaceRule,
      });

      if (
        result.item.paperAssignmentSource === 'MANUAL'
        && result.item.paperReviewStatus !== 'CONFIRMED'
      ) {
        setConfirmingItemId(row.itemId);
        await confirmEtsyImportedItemPaper(row.itemId);
      }

      await loadData({ silent: true });
      setSelectedPaperByItemId((current) => {
        const next = { ...current };
        delete next[row.itemId];
        return next;
      });

      toast({
        title: shouldReplaceRule
          ? t('integrations.etsy.toasts.paperRuleUpdatedTitle', 'Paper rule updated')
          : t('integrations.etsy.toasts.paperAppliedTitle', 'Paper applied'),
        description: shouldReplaceRule
          ? t('integrations.etsy.toasts.paperRuleUpdatedDescription', '{{count}} similar items were refreshed for future review. This item was confirmed automatically.', { count: result.affectedItemCount })
          : t('integrations.etsy.toasts.paperAppliedDescription', 'This Etsy item was confirmed automatically.'),
      });
    } catch (saveError) {
      logging.error('Failed to save Etsy imported item paper', { error: saveError, itemId: row.itemId });
      toast({
        title: t('integrations.etsy.toasts.applyPaperFailedTitle', 'Could not apply paper'),
        description: saveError instanceof Error ? saveError.message : 'Failed to save Etsy paper selection.',
        variant: 'destructive',
      });
    } finally {
      setSavingItemId(null);
      setConfirmingItemId(null);
    }
  }, [loadData, paperRules, toast]);

  const handleConfirmItemPaper = useCallback(async (row: EtsyItemMatchRow) => {
    if (!row.matchedPaperType?.id) {
      toast({
        title: t('integrations.etsy.toasts.noPaperSelectedTitle', 'No paper selected'),
        description: t('integrations.etsy.toasts.noPaperSelectedDescription', 'Apply a paper before confirming this Etsy item.'),
        variant: 'destructive',
      });
      return;
    }

    try {
      setConfirmingItemId(row.itemId);
      await confirmEtsyImportedItemPaper(row.itemId);
      await loadData({ silent: true });
      toast({
        title: t('integrations.etsy.toasts.paperConfirmedTitle', 'Paper confirmed'),
        description: t('integrations.etsy.toasts.paperConfirmedDescription', 'This Etsy item is marked as reviewed.'),
      });
    } catch (confirmError) {
      logging.error('Failed to confirm Etsy imported item paper', { error: confirmError, itemId: row.itemId });
      toast({
        title: t('integrations.etsy.toasts.confirmPaperFailedTitle', 'Could not confirm paper'),
        description: confirmError instanceof Error ? confirmError.message : 'Failed to confirm Etsy paper selection.',
        variant: 'destructive',
      });
    } finally {
      setConfirmingItemId(null);
    }
  }, [loadData, toast]);

  const handleSaveItemSize = useCallback(async (row: EtsyItemMatchRow) => {
    const draft = draftSizeByItemId[row.itemId];
    const widthIn = Number(draft?.widthIn ?? row.matchedWidthIn ?? row.primarySizeValueMapping?.normalizedWidthIn ?? 0);
    const heightIn = Number(draft?.heightIn ?? row.matchedHeightIn ?? row.primarySizeValueMapping?.normalizedHeightIn ?? 0);

    if (!Number.isFinite(widthIn) || widthIn <= 0 || !Number.isFinite(heightIn) || heightIn <= 0) {
      toast({
        title: t('integrations.etsy.toasts.invalidSizeTitle', 'Invalid size'),
        description: t('integrations.etsy.toasts.invalidSizeDescription', 'Width and height must be positive inches.'),
        variant: 'destructive',
      });
      return;
    }

    try {
      setSavingSizeItemId(row.itemId);
      await saveEtsyImportedItemSize(row.itemId, { widthIn, heightIn });
      await loadData({ silent: true });
      setDraftSizeByItemId((current) => {
        const next = { ...current };
        delete next[row.itemId];
        return next;
      });
      setEditingSizeItemId((current) => (current === row.itemId ? null : current));
      toast({
        title: t('integrations.etsy.toasts.sizeSavedTitle', 'Size saved'),
        description: t('integrations.etsy.toasts.sizeSavedDescription', 'Normalized size was stored on this Etsy item.'),
      });
    } catch (saveError) {
      logging.error('Failed to save Etsy imported item size', { error: saveError, itemId: row.itemId });
      toast({
        title: t('integrations.etsy.toasts.saveSizeFailedTitle', 'Could not save size'),
        description: saveError instanceof Error ? saveError.message : 'Failed to save Etsy item size.',
        variant: 'destructive',
      });
    } finally {
      setSavingSizeItemId(null);
    }
  }, [draftSizeByItemId, loadData, toast]);

  const handleStartSizeEdit = useCallback((row: EtsyItemMatchRow) => {
    const width = row.matchedWidthIn ?? row.primarySizeValueMapping?.normalizedWidthIn ?? null;
    const height = row.matchedHeightIn ?? row.primarySizeValueMapping?.normalizedHeightIn ?? null;

    setDraftSizeByItemId((current) => ({
      ...current,
      [row.itemId]: {
        widthIn: width != null ? width.toString() : '',
        heightIn: height != null ? height.toString() : '',
      },
    }));
    setEditingSizeItemId(row.itemId);
  }, []);

  const handleCancelSizeEdit = useCallback((itemId: string) => {
    setDraftSizeByItemId((current) => {
      const next = { ...current };
      delete next[itemId];
      return next;
    });
    setEditingSizeItemId((current) => (current === itemId ? null : current));
  }, []);

  const handleSizeDraftChange = useCallback((
    row: EtsyItemMatchRow,
    dimension: 'widthIn' | 'heightIn',
    rawValue: string,
  ) => {
    const fallbackWidth = row.matchedWidthIn ?? row.primarySizeValueMapping?.normalizedWidthIn ?? null;
    const fallbackHeight = row.matchedHeightIn ?? row.primarySizeValueMapping?.normalizedHeightIn ?? null;
    const currentDraft = draftSizeByItemId[row.itemId] ?? {
      widthIn: fallbackWidth != null ? fallbackWidth.toString() : '',
      heightIn: fallbackHeight != null ? fallbackHeight.toString() : '',
    };

    const nextDraft = {
      widthIn: currentDraft.widthIn,
      heightIn: currentDraft.heightIn,
    };

    const baseWidth = Number(currentDraft.widthIn || fallbackWidth || 0);
    const baseHeight = Number(currentDraft.heightIn || fallbackHeight || 0);
    const aspectRatio = baseWidth > 0 && baseHeight > 0 ? baseWidth / baseHeight : null;

    nextDraft[dimension] = rawValue;

    const numericValue = Number(rawValue);
    if (aspectRatio && Number.isFinite(numericValue) && numericValue > 0) {
      if (dimension === 'widthIn') {
        nextDraft.heightIn = (numericValue / aspectRatio).toFixed(2);
      } else {
        nextDraft.widthIn = (numericValue * aspectRatio).toFixed(2);
      }
    }

    setDraftSizeByItemId((current) => ({
      ...current,
      [row.itemId]: nextDraft,
    }));
  }, [draftSizeByItemId]);

  const handleAddressDraftChange = useCallback((
    orderId: string,
    field: keyof EtsyAddressDraft,
    value: string | boolean,
  ) => {
    setAddressDraftByOrderId((current) => {
      const existing = current[orderId];
      if (!existing) {
        return current;
      }

      return {
        ...current,
        [orderId]: {
          ...existing,
          [field]: typeof value === 'string' && field === 'countryCode'
            ? value.toUpperCase()
            : value,
        },
      };
    });
  }, []);

  const handleAddressCityInputChange = useCallback(async (
    orderId: string,
    countryCode: string,
    value: string,
  ) => {
    handleAddressDraftChange(orderId, 'city', value);
    handleAddressDraftChange(orderId, 'novaposhtaCityRef', '');

    if (!hasNovapostPresence(countryCode)) {
      setAddressCitySuggestionsByOrderId((current) => ({ ...current, [orderId]: [] }));
      setAddressCitySuggestionsOpenOrderId((current) => (current === orderId ? null : current));
      return;
    }

    const query = value.trim();
    if (query.length < 2) {
      setAddressCitySuggestionsByOrderId((current) => ({ ...current, [orderId]: [] }));
      setAddressCitySuggestionsOpenOrderId(orderId);
      return;
    }

    try {
      setLoadingAddressCitySuggestionsOrderId(orderId);
      const suggestions = await getShippingCities('NOVAPOST', {
        countryCode,
        query,
      });
      setAddressCitySuggestionsByOrderId((current) => ({
        ...current,
        [orderId]: suggestions,
      }));
      setAddressCitySuggestionsOpenOrderId(orderId);
    } catch (error) {
      logging.error('Failed to load Nova Post city suggestions for Etsy address', { error, orderId, countryCode, query });
      setAddressCitySuggestionsByOrderId((current) => ({ ...current, [orderId]: [] }));
    } finally {
      setLoadingAddressCitySuggestionsOrderId((current) => (current === orderId ? null : current));
    }
  }, [handleAddressDraftChange]);

  const handleAddressCitySuggestionSelect = useCallback((orderId: string, suggestion: ShippingAddressSuggestion) => {
    const primaryCityName = suggestion.label.split(',')[0]?.trim() || suggestion.label.trim();
    setAddressDraftByOrderId((current) => {
      const existing = current[orderId];
      if (!existing) {
        return current;
      }

      return {
        ...current,
        [orderId]: {
          ...existing,
          city: primaryCityName,
          novaposhtaCityRef: suggestion.id,
        },
      };
    });
    setAddressCitySuggestionsOpenOrderId((current) => (current === orderId ? null : current));
  }, []);

  const parseRawShipmentAddress = useCallback(async (orderId: string, rawShipmentAddressText: string) => {
    try {
      setParsingRawAddressOrderId(orderId);
      const parsed = await parseEtsyImportedOrderRawShipmentAddress(orderId, rawShipmentAddressText);
      setAddressDraftByOrderId((current) => {
        const order = orders.find((entry) => entry.id === orderId);
        const existing = current[orderId] ?? (order ? buildAddressDraftFromOrder(order) : {
          countryCode: '',
          firstName: '',
          lastName: '',
          phone: '',
          street1: '',
          street2: '',
          city: '',
          region: '',
          postalCode: '',
          setAsDefault: false,
        });

        return {
          ...current,
          [orderId]: {
            ...existing,
            ...parsed.fields,
            setAsDefault: false,
          },
        };
      });
      setRawShipmentAddressByOrderId((current) => ({
        ...current,
        [orderId]: parsed.rawShipmentAddressText,
      }));
    } catch (parseError) {
      logging.error('Failed to parse Etsy raw shipment address', { error: parseError, orderId });
      toast({
        title: t('integrations.etsy.toasts.parseAddressFailedTitle', 'Could not parse address'),
        description: parseError instanceof Error ? parseError.message : 'Failed to parse pasted Etsy shipment address.',
        variant: 'destructive',
      });
    } finally {
      setParsingRawAddressOrderId((current) => (current === orderId ? null : current));
    }
  }, [orders, t, toast]);

  const handleRawShipmentAddressChange = useCallback((orderId: string, value: string) => {
    setRawShipmentAddressByOrderId((current) => ({
      ...current,
      [orderId]: value,
    }));

    const existingTimer = rawAddressParseTimersRef.current[orderId];
    if (existingTimer) {
      window.clearTimeout(existingTimer);
    }

    rawAddressParseTimersRef.current[orderId] = window.setTimeout(() => {
      rawAddressParseTimersRef.current[orderId] = undefined;
      void parseRawShipmentAddress(orderId, value);
    }, 2000);
  }, [parseRawShipmentAddress]);

  const handleParseRawShipmentAddressNow = useCallback((orderId: string) => {
    const existingTimer = rawAddressParseTimersRef.current[orderId];
    if (existingTimer) {
      window.clearTimeout(existingTimer);
      rawAddressParseTimersRef.current[orderId] = undefined;
    }

    const value = rawShipmentAddressByOrderId[orderId] ?? '';
    void parseRawShipmentAddress(orderId, value);
  }, [parseRawShipmentAddress, rawShipmentAddressByOrderId]);

  const validateNovapostAddressCitySelection = useCallback(async (
    orderId: string,
    draft: EtsyAddressDraft,
  ): Promise<{ valid: boolean; message?: string }> => {
    if (!hasNovapostPresence(draft.countryCode) || !draft.novaposhtaCityRef?.trim() || draft.city.trim().length < 2) {
      return { valid: true };
    }

    setValidatingAddressCityOrderId(orderId);

    try {
      const suggestions = await getShippingCities('NOVAPOST', {
        countryCode: draft.countryCode,
        query: draft.city.trim(),
      });
      setAddressCitySuggestionsByOrderId((current) => ({
        ...current,
        [orderId]: suggestions,
      }));

      const normalizedCity = normalizeSuggestionName(draft.city);
      const matchedSuggestion = suggestions.find((suggestion) => {
        const suggestionLabel = suggestion.label.split(',')[0]?.trim() || suggestion.label.trim();
        return suggestion.id === draft.novaposhtaCityRef
          && normalizeSuggestionName(suggestionLabel) === normalizedCity;
      });

      if (matchedSuggestion) {
        return { valid: true };
      }

      setAddressDraftByOrderId((current) => {
        const existing = current[orderId];
        if (!existing) {
          return current;
        }

        return {
          ...current,
          [orderId]: {
            ...existing,
            novaposhtaCityRef: undefined,
          },
        };
      });
      setAddressCitySuggestionsOpenOrderId(orderId);
      return {
        valid: false,
        message: 'Select a city from Nova Post suggestions.',
      };
    } catch (error) {
      logging.error('Failed to validate Nova Post city for Etsy address', { error, orderId, countryCode: draft.countryCode, city: draft.city });
      return {
        valid: false,
        message: 'Could not verify the city. Try selecting it again.',
      };
    } finally {
      setValidatingAddressCityOrderId((current) => (current === orderId ? null : current));
    }
  }, []);

  const handleResetAddressDraft = useCallback((order: EtsyPendingOrder) => {
    setAddressDraftByOrderId((current) => ({
      ...current,
      [order.id]: buildAddressDraftFromOrder({
        ...order,
        matchedAddress: null,
        addressReviewStatus: null,
        addressAssignedAt: null,
        addressConfirmedAt: null,
      }),
    }));
    setAddressCitySuggestionsByOrderId((current) => ({ ...current, [order.id]: [] }));
    setAddressCitySuggestionsOpenOrderId((current) => (current === order.id ? null : current));
  }, []);

  const handleConfirmOrderAddress = useCallback(async (order: EtsyPendingOrder) => {
    const draft = addressDraftByOrderId[order.id] ?? buildAddressDraftFromOrder(order);
    const errors = validateAddressDraft(draft, t);

    if (hasAddressDraftErrors(errors)) {
      toast({
        title: t('integrations.etsy.toasts.completeAddressFirstTitle', 'Complete the address first'),
        description: Object.values(errors)[0] ?? 'Shipping address is incomplete.',
        variant: 'destructive',
      });
      return;
    }

    const novapostValidation = await validateNovapostAddressCitySelection(order.id, draft);
    if (!novapostValidation.valid) {
      toast({
        title: t('integrations.etsy.toasts.completeAddressFirstTitle', 'Complete the address first'),
        description: novapostValidation.message ?? 'Shipping address is incomplete.',
        variant: 'destructive',
      });
      return;
    }

    const normalizedDraft: ShippingAddress = {
      countryCode: draft.countryCode.trim().toUpperCase(),
      firstName: draft.firstName.trim(),
      lastName: draft.lastName.trim(),
      phone: draft.phone.trim(),
      street1: draft.street1.trim(),
      street2: draft.street2?.trim() || undefined,
      city: draft.city.trim(),
      novaposhtaCityRef: draft.novaposhtaCityRef?.trim() || undefined,
      region: draft.region.trim(),
      postalCode: draft.postalCode.trim(),
      setAsDefault: false,
    };

    const needsSave = !isDraftEqualToMatchedAddress(order, normalizedDraft);

    try {
      setConfirmingAddressOrderId(order.id);
      if (needsSave) {
        setSavingAddressOrderId(order.id);
        await saveEtsyImportedOrderAddress(order.id, {
          shippingAddress: normalizedDraft,
        });
        setAddressDraftByOrderId((current) => ({
          ...current,
          [order.id]: normalizedDraft,
        }));
      }
      await confirmEtsyImportedOrderAddress(order.id);
      await loadData({ silent: true });
      toast({
        title: t('integrations.etsy.toasts.addressConfirmedTitle', 'Address confirmed'),
        description: t('integrations.etsy.toasts.addressConfirmedDescription', 'This Etsy receipt is marked as reviewed for shipping.'),
      });
    } catch (confirmError) {
      logging.error('Failed to confirm Etsy imported order address', { error: confirmError, orderId: order.id });
      toast({
        title: t('integrations.etsy.toasts.confirmAddressFailedTitle', 'Could not confirm address'),
        description: confirmError instanceof Error ? confirmError.message : 'Failed to confirm Etsy receipt address.',
        variant: 'destructive',
      });
    } finally {
      setSavingAddressOrderId(null);
      setConfirmingAddressOrderId(null);
    }
  }, [addressDraftByOrderId, loadData, toast, validateNovapostAddressCitySelection]);

  const handleConfirmMatch = useCallback(async (itemId: string) => {
    const order = orders.find(o => o.items.some(i => i.id === itemId));
    const item = order?.items.find(i => i.id === itemId);
    if (!item || !item.fullSizeUploadedFile) return;

    try {
      setConfirmingItemId(itemId);
      await assignImportedItemFullSizeImage({
        userId: '',
        importedItemId: itemId,
        uploadedFileId: item.fullSizeUploadedFile.id,
      });

      await loadData({ silent: true });

      toast({
        title: t('integrations.etsy.toasts.matchConfirmedTitle', 'Match confirmed'),
        description: t('integrations.etsy.toasts.matchConfirmedDescription', 'The auto-linked image was manually confirmed.'),
      });
    } catch (confirmError) {
      logging.error('Failed to confirm Etsy item image match', { error: confirmError, itemId });
      toast({
        title: t('integrations.etsy.toasts.confirmMatchFailedTitle', 'Could not confirm match'),
        description: confirmError instanceof Error ? confirmError.message : 'Failed to confirm image match.',
        variant: 'destructive',
      });
    } finally {
      setConfirmingItemId(null);
    }
  }, [loadData, orders, toast]);

  const [isConfirmingAllImages, setIsConfirmingAllImages] = useState(false);

  const autoLinkedImageCount = useMemo(() => 
    filteredImageRows.filter(({ item }) => item.fullSizeImageSource === 'REUSED').length,
  [filteredImageRows]);

  const handleConfirmAllImageMatches = useCallback(async () => {
    const toConfirm = filteredImageRows.filter(({ item }) => item.fullSizeImageSource === 'REUSED');
    if (toConfirm.length === 0) return;

    setIsConfirmingAllImages(true);
    let successCount = 0;
    try {
      for (const { item } of toConfirm) {
        if (!item.fullSizeUploadedFile) continue;
        try {
          await assignImportedItemFullSizeImage({
            userId: '',
            importedItemId: item.id,
            uploadedFileId: item.fullSizeUploadedFile.id,
          });
          successCount++;
        } catch (err) {
          logging.error('Failed to bulk confirm image match', { error: err, itemId: item.id });
        }
      }

      await loadData({ silent: true });

      toast({
        title: t('integrations.etsy.toasts.bulkMatchConfirmedTitle', 'Bulk matching finished'),
        description: t('integrations.etsy.toasts.bulkMatchConfirmedDescription', 'Successfully confirmed {{count}} image matches.', { count: successCount }),
      });
    } finally {
      setIsConfirmingAllImages(false);
    }
  }, [filteredImageRows, loadData, toast]);

  const uploadedImageSelectorPanel = uploadedImageSelectorItem ? (
    <div className="mb-4 space-y-5 rounded-lg border border-border bg-muted/10 p-4">
      <div className="border-b border-border pb-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">
            {t('integrations.etsy.matching.uploadedImageSelector.title', 'Use uploaded images')}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('integrations.etsy.matching.uploadedImageSelector.description', 'Select a previously uploaded full-size image for this Etsy draft.')}
          </p>
        </div>
      </div>

      <div className="grid gap-4 border-b border-border pb-5 md:grid-cols-[160px_1fr]">
        <div className="h-40 w-40 overflow-hidden rounded-md border border-border bg-muted/30">
          {uploadedImageCandidates?.draft.previewUrl ?? uploadedImageSelectorItem.previewUrl ? (
            <img
              src={uploadedImageCandidates?.draft.previewUrl ?? uploadedImageSelectorItem.previewUrl ?? undefined}
              alt={uploadedImageSelectorItem.title}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center px-3 text-center text-xs text-muted-foreground">
              {t('integrations.etsy.matching.noDraftPreview', 'No draft preview')}
            </div>
          )}
        </div>
        <div className="min-w-0 space-y-3">
          <div>
            <div className="text-sm font-medium text-foreground">{uploadedImageSelectorItem.title}</div>
            <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
              {formatExpectedPrintSize(uploadedImageSelectorItem.matchedWidthIn, uploadedImageSelectorItem.matchedHeightIn) ? (
                <Badge variant="outline" className={getMatchingTagClassName('info')}>
                  {t('integrations.etsy.matching.massMatch.expectedSize', 'Expected')}: {formatExpectedPrintSize(uploadedImageSelectorItem.matchedWidthIn, uploadedImageSelectorItem.matchedHeightIn)}
                </Badge>
              ) : (
                <Badge variant="outline" className={getMatchingTagClassName('warning')}>
                  {t('integrations.etsy.matching.uploadedImageSelector.noExpectedSize', 'Expected size missing')}
                </Badge>
              )}
              {uploadedImageSelectorItem.imageOriginalWidthPx && uploadedImageSelectorItem.imageOriginalHeightPx ? (
                <Badge variant="outline" className={getMatchingTagClassName('neutral')}>
                  {uploadedImageSelectorItem.imageOriginalWidthPx} x {uploadedImageSelectorItem.imageOriginalHeightPx}px
                </Badge>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch
                id="show-all-uploaded-images"
                checked={showAllUploadedImages}
                onCheckedChange={(checked) => handleToggleShowAllUploadedImages(checked)}
                disabled={uploadedImageCandidatesStatus === 'loading'}
              />
              <label htmlFor="show-all-uploaded-images" className="text-sm font-medium text-foreground">
                {t('integrations.etsy.matching.uploadedImageSelector.showAll', 'Show all uploaded images')}
              </label>
            </div>
            <div className="text-xs text-muted-foreground">
              {showAllUploadedImages
                ? t('integrations.etsy.matching.uploadedImageSelector.showingAll', 'Showing all full-size uploads')
                : t('integrations.etsy.matching.uploadedImageSelector.showingLikely', 'Showing matches above {{percent}} confidence', {
                    percent: formatPercent(uploadedImageCandidates?.threshold ?? 0.7),
                  })}
            </div>
          </div>
        </div>
      </div>

      {uploadedImageCandidatesStatus === 'loading' ? (
        <div className="flex min-h-[220px] items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {t('integrations.etsy.matching.uploadedImageSelector.loading', 'Loading uploaded images...')}
        </div>
      ) : uploadedImageCandidatesStatus === 'error' ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {uploadedImageCandidatesError ?? t('integrations.etsy.matching.uploadedImageSelector.loadError', 'Could not load uploaded images.')}
        </div>
      ) : uploadedImageCandidates && uploadedImageCandidates.candidates.length > 0 ? (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {uploadedImageCandidates.candidates.map((candidate) => {
              const dimensions = candidate.originalWidthPx && candidate.originalHeightPx
                ? `${candidate.originalWidthPx} x ${candidate.originalHeightPx}px`
                : null;
              const realSize = candidate.realPrintSize
                ? `${formatPrintSizeIn(candidate.realPrintSize.widthIn)} x ${formatPrintSizeIn(candidate.realPrintSize.heightIn)} in`
                : null;
              const isAssigning = assigningUploadedImageId === candidate.id;
              const confidenceThreshold = uploadedImageCandidates.threshold;

              return (
                <div key={candidate.id} className="flex min-h-[320px] flex-col rounded-lg border border-border bg-background p-3">
                  <button
                    type="button"
                    className="relative aspect-square overflow-hidden rounded-md border border-border bg-muted/30 text-left transition hover:border-primary/60"
                    onClick={() => {
                      setPreviewMeta({
                        widthIn: null,
                        heightIn: null,
                        marginIn: null,
                        originalWidthPx: candidate.originalWidthPx,
                        originalHeightPx: candidate.originalHeightPx,
                      });
                      void handleOpenUploadedFile(candidate.id);
                    }}
                  >
                    <img src={candidate.thumbnailUrl} alt={candidate.filename} className="h-full w-full object-cover" />
                    <span className="absolute bottom-2 right-2 inline-flex items-center rounded bg-black/65 px-2 py-1 text-[11px] font-medium text-white">
                      <Eye className="mr-1 h-3 w-3" />
                      {t('integrations.etsy.matching.preview', 'Preview')}
                    </span>
                  </button>
                  <div className="mt-3 min-w-0 flex-1 space-y-2">
                    <div className="truncate text-sm font-medium text-foreground" title={candidate.filename}>{candidate.filename}</div>
                    <div className="flex flex-wrap gap-1.5">
                      <Badge
                        variant="outline"
                        className={getMatchingTagClassName(
                          candidate.visualSimilarity != null && candidate.visualSimilarity >= confidenceThreshold ? 'success' : 'neutral',
                        )}
                      >
                        {t('integrations.etsy.matching.uploadedImageSelector.confidence', 'Confidence')}: {formatPercent(candidate.visualSimilarity) ?? t('integrations.etsy.common.notAvailable', 'n/a')}
                      </Badge>
                      {candidate.sizeSimilarity != null ? (
                        <Badge variant="outline" className={getMatchingTagClassName('info')}>
                          {t('integrations.etsy.matching.uploadedImageSelector.sizeFit', 'Size fit')}: {formatPercent(candidate.sizeSimilarity)}
                        </Badge>
                      ) : null}
                    </div>
                    <div className="space-y-1 text-xs text-muted-foreground">
                      {dimensions ? <div>{dimensions}</div> : null}
                      {realSize ? (
                        <div>
                          {t('integrations.etsy.matching.massMatch.realSizeAt300Dpi', 'Real at 300 PPI')}: {realSize}
                        </div>
                      ) : null}
                      {formatFileSize(candidate.sizeBytes) ? <div>{formatFileSize(candidate.sizeBytes)}</div> : null}
                    </div>
                  </div>
                  <Button
                    type="button"
                    className="mt-3 w-full"
                    onClick={() => void handleAssignUploadedImageCandidate(candidate.id)}
                    disabled={assigningUploadedImageId != null}
                  >
                    {isAssigning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                    {t('integrations.etsy.matching.uploadedImageSelector.useThisImage', 'Use this image')}
                  </Button>
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
            <div className="text-sm text-muted-foreground">
              {t('integrations.etsy.matching.uploadedImageSelector.pageStatus', 'Page {{page}} of {{totalPages}} · {{total}} images', {
                page: uploadedImageCandidates.page,
                totalPages: uploadedImageCandidates.totalPages,
                total: uploadedImageCandidates.total,
              })}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleUploadedImageCandidatePageChange(Math.max(uploadedImageCandidates.page - 1, 1))}
                disabled={uploadedImageCandidates.page <= 1}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                {t('integrations.etsy.matching.uploadedImageSelector.previous', 'Previous')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleUploadedImageCandidatePageChange(Math.min(uploadedImageCandidates.page + 1, uploadedImageCandidates.totalPages))}
                disabled={uploadedImageCandidates.page >= uploadedImageCandidates.totalPages}
              >
                {t('integrations.etsy.matching.uploadedImageSelector.next', 'Next')}
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex min-h-[220px] items-center justify-center rounded-md border border-dashed border-border px-4 text-center text-sm text-muted-foreground">
          {showAllUploadedImages
            ? t('integrations.etsy.matching.uploadedImageSelector.emptyAll', 'No uploaded full-size images are available yet.')
            : t('integrations.etsy.matching.uploadedImageSelector.emptyLikely', 'No uploaded images are above the confidence threshold. Turn on Show all uploaded images to browse everything.')}
        </div>
      )}
    </div>
  ) : null;

  const handleOpenUploadedFile = useCallback(async (fileId: string) => {
    try {
      const signed = await getSignedUrlForFile(fileId);
      setPreviewImageUrl(signed.signedUrl);
      setIsPreviewOpen(true);
    } catch (openError) {
      logging.error('Failed to open Etsy full-size image', { error: openError, fileId });
      toast({
        title: t('integrations.etsy.toasts.openImageFailedTitle', 'Could not open image'),
        description: openError instanceof Error ? openError.message : 'Failed to open the uploaded image.',
        variant: 'destructive',
      });
    }
  }, [toast]);

  const closePreview = useCallback(() => {
    setIsPreviewOpen(false);
    setPreviewImageUrl(null);
    setPreviewMeta(null);
  }, []);

  const loadUploadedImageCandidates = useCallback(async (
    itemId: string,
    options?: { readonly page?: number; readonly showAll?: boolean },
  ) => {
    const nextPage = options?.page ?? uploadedImageCandidatesPage;
    const nextShowAll = options?.showAll ?? showAllUploadedImages;

    try {
      setUploadedImageCandidatesStatus('loading');
      setUploadedImageCandidatesError(null);
      const candidatePage = await fetchEtsyUploadedImageCandidates(itemId, {
        page: nextPage,
        pageSize: UPLOADED_IMAGE_CANDIDATE_PAGE_SIZE,
        showAll: nextShowAll,
      });
      setUploadedImageCandidates(candidatePage);
      setUploadedImageCandidatesPage(candidatePage.page);
    } catch (candidateError) {
      logging.error('Failed to load uploaded image candidates', { error: candidateError, itemId });
      setUploadedImageCandidatesStatus('error');
      setUploadedImageCandidatesError(candidateError instanceof Error ? candidateError.message : 'Failed to load uploaded images.');
      return;
    }

    setUploadedImageCandidatesStatus('idle');
  }, [showAllUploadedImages, uploadedImageCandidatesPage]);

  const openUploadedImageSelector = useCallback((item: EtsyPendingOrder['items'][number]) => {
    const shouldShowAllByDefault = !item.previewUrl;
    setUploadedImageSelectorItem(item);
    setUploadedImageCandidates(null);
    setUploadedImageCandidatesError(null);
    setShowAllUploadedImages(shouldShowAllByDefault);
    setUploadedImageCandidatesPage(1);
    void loadUploadedImageCandidates(item.id, { page: 1, showAll: shouldShowAllByDefault });
  }, [loadUploadedImageCandidates]);

  const closeUploadedImageSelector = useCallback(() => {
    setUploadedImageSelectorItem(null);
    setUploadedImageCandidates(null);
    setUploadedImageCandidatesError(null);
    setUploadedImageCandidatesStatus('idle');
    setAssigningUploadedImageId(null);
  }, []);

  const handleToggleShowAllUploadedImages = useCallback((checked: boolean) => {
    if (!uploadedImageSelectorItem) {
      return;
    }
    setShowAllUploadedImages(checked);
    setUploadedImageCandidatesPage(1);
    void loadUploadedImageCandidates(uploadedImageSelectorItem.id, { page: 1, showAll: checked });
  }, [loadUploadedImageCandidates, uploadedImageSelectorItem]);

  const handleUploadedImageCandidatePageChange = useCallback((nextPage: number) => {
    if (!uploadedImageSelectorItem) {
      return;
    }
    setUploadedImageCandidatesPage(nextPage);
    void loadUploadedImageCandidates(uploadedImageSelectorItem.id, { page: nextPage });
  }, [loadUploadedImageCandidates, uploadedImageSelectorItem]);

  const handleAssignUploadedImageCandidate = useCallback(async (uploadedFileId: string) => {
    if (!uploadedImageSelectorItem) {
      return;
    }

    try {
      setAssigningUploadedImageId(uploadedFileId);
      const result = await assignImportedItemFullSizeImage({
        userId: '',
        importedItemId: uploadedImageSelectorItem.id,
        uploadedFileId,
      });
      await loadData({ silent: true });
      closeUploadedImageSelector();
      toast({
        title: t('integrations.etsy.toasts.existingFileLinkedTitle', 'Existing file linked'),
        description: result.affectedItemCount > 1
          ? t('integrations.etsy.toasts.imageAppliedToManyDescription', 'Applied to {{count}} Etsy items that share this image source.', { count: result.affectedItemCount })
          : t('integrations.etsy.toasts.imageLinkedDescription', 'The uploaded file is now linked to this Etsy item.'),
      });
    } catch (assignError) {
      logging.error('Failed to assign uploaded image candidate', {
        error: assignError,
        itemId: uploadedImageSelectorItem.id,
        uploadedFileId,
      });
      toast({
        title: t('integrations.etsy.toasts.assignImageFailedTitle', 'Could not use uploaded image'),
        description: assignError instanceof Error ? assignError.message : 'Failed to link the uploaded image.',
        variant: 'destructive',
      });
    } finally {
      setAssigningUploadedImageId(null);
    }
  }, [closeUploadedImageSelector, loadData, toast, uploadedImageSelectorItem]);

  const handleUploadItemImage = useCallback(async (
    item: EtsyPendingOrder['items'][number],
    file: File | null,
  ) => {
    if (!file) {
      return;
    }

    try {
      setUploadingImageItemId(item.id);
      const result = await uploadEtsyImportedItemFullSizeImage(item.id, file);
      await loadData({ silent: true });
      toast({
        title: result.duplicate
          ? t('integrations.etsy.toasts.existingFileLinkedTitle', 'Existing file linked')
          : item.fullSizeUploadedFile
            ? t('integrations.etsy.toasts.imageReplacedTitle', 'Image replaced')
            : t('integrations.etsy.toasts.fullSizeUploadedTitle', 'Full-size image uploaded'),
        description: result.affectedItemCount > 1
          ? t('integrations.etsy.toasts.imageAppliedToManyDescription', 'Applied to {{count}} Etsy items that share this image source.', { count: result.affectedItemCount })
          : t('integrations.etsy.toasts.imageLinkedDescription', 'The uploaded file is now linked to this Etsy item.'),
      });
    } catch (uploadError) {
      logging.error('Failed to upload Etsy full-size image', { error: uploadError, itemId: item.id });
      toast({
        title: t('integrations.etsy.toasts.uploadImageFailedTitle', 'Could not upload image'),
        description: uploadError instanceof Error ? uploadError.message : 'Failed to upload Etsy full-size image.',
        variant: 'destructive',
      });
    } finally {
      setUploadingImageItemId(null);
      const input = imageInputRefs.current[item.id];
      if (input) {
        input.value = '';
      }
    }
  }, [loadData, toast]);

  const handleFetchDraftImage = useCallback(async (itemId: string) => {
    try {
      setFetchingDraftImageItemId(itemId);
      await fetchEtsyImportedItemImage(itemId);
      await loadData({ silent: true });
    } catch (fetchError) {
      logging.error('Failed to fetch Etsy imported draft image in matching workspace', {
        error: fetchError,
        itemId,
      });
      toast({
        title: t('integrations.etsy.toasts.fetchDraftImageFailedTitle', 'Could not upload Etsy draft image'),
        description: fetchError instanceof Error ? fetchError.message : 'Failed to fetch the Etsy draft image.',
        variant: 'destructive',
      });
      await loadData({ silent: true });
    } finally {
      draftImageBatchRef.current.completed += 1;
      setFetchingDraftImageItemId(null);
    }
  }, [loadData, toast]);

  useEffect(() => {
    if (fetchingDraftImageItemId || queuedDraftImageItemIds.length === 0) {
      return;
    }

    const [nextItemId, ...rest] = queuedDraftImageItemIds;
    setQueuedDraftImageItemIds(rest);
    void handleFetchDraftImage(nextItemId);
  }, [fetchingDraftImageItemId, handleFetchDraftImage, queuedDraftImageItemIds]);

  useEffect(() => {
    const batch = draftImageBatchRef.current;
    if (!batch.active || fetchingDraftImageItemId || queuedDraftImageItemIds.length > 0) {
      return;
    }

    batch.active = false;
    toast({
      title: t('integrations.etsy.toasts.draftUploadFinishedTitle', 'Etsy draft upload finished'),
      description: batch.total === 1
        ? t('integrations.etsy.toasts.draftUploadFinishedOne', '1 Etsy draft image was uploaded to TulasiHome storage.')
        : t('integrations.etsy.toasts.draftUploadFinishedMany', '{{count}} Etsy draft images were uploaded to TulasiHome storage.', { count: batch.completed }),
    });
  }, [fetchingDraftImageItemId, queuedDraftImageItemIds.length, toast]);

  useEffect(() => {
    if (activeTab !== MATCHING_TABS.images || status !== 'idle' || fetchingDraftImageItemId || queuedDraftImageItemIds.length > 0) {
      return;
    }

    const missingDraftImageItemIds = orders
      .flatMap((order) => order.items)
      .filter((item) => (
        item.imageAvailability.status === 'missing'
        && !autoAttemptedDraftImageItemIdsRef.current.has(item.id)
      ))
      .map((item) => item.id);

    if (missingDraftImageItemIds.length === 0) {
      return;
    }

    for (const itemId of missingDraftImageItemIds) {
      autoAttemptedDraftImageItemIdsRef.current.add(itemId);
    }

    draftImageBatchRef.current = {
      active: true,
      total: missingDraftImageItemIds.length,
      completed: 0,
    };

    toast({
      title: t('integrations.etsy.toasts.uploadingDraftImagesTitle', 'Uploading Etsy draft images'),
      description: missingDraftImageItemIds.length === 1
        ? t('integrations.etsy.toasts.uploadingDraftImagesOne', 'TulasiHome started uploading 1 Etsy draft image to storage.')
        : t('integrations.etsy.toasts.uploadingDraftImagesMany', 'TulasiHome started uploading {{count}} Etsy draft images to storage.', { count: missingDraftImageItemIds.length }),
    });

    setQueuedDraftImageItemIds(missingDraftImageItemIds);
  }, [activeTab, fetchingDraftImageItemId, orders, queuedDraftImageItemIds.length, status, toast]);

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <Card className="border-border/70">
        <CardHeader className="gap-4">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Store className="h-3.5 w-3.5" />
                {t('integrations.etsy.matching.badge', 'Etsy mass matching')}
              </div>
              <div>
                <CardTitle className="text-xl">
                  {t('integrations.etsy.matchingTitle', 'Etsy Matching Workspace')}
                </CardTitle>
                <CardDescription className="mt-1 max-w-3xl">
                  {t('integrations.etsy.matching.description', 'Variation Mapping is the upstream step. AI first decides whether each shop-specific Etsy variation means size, paper, or unknown, then Sizes and Papers apply production mappings on top of that interpretation.')}
                </CardDescription>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => void loadData()} disabled={status === 'loading'}>
                {status === 'loading' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                {t('common.reload', 'Reload')}
              </Button>
            </div>
          </div>
          <EtsyWorkspaceNav activeMode={WORKSPACE_CANVAS_MODES.etsyMatching} activeMatchingTab={activeTab} />
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-7">
            <div className="flex min-h-[84px] flex-col rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5">
              <div className="text-sm font-medium text-foreground">{t('integrations.etsy.matching.metrics.importedOrders', 'Imported orders')}</div>
              <div className="mt-auto pt-1.5 text-2xl font-semibold leading-none text-foreground">{metrics.totalOrders}</div>
            </div>
            <div className="flex min-h-[84px] flex-col rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5">
              <div className="text-sm font-medium text-foreground">{t('integrations.etsy.matching.metrics.importedItems', 'Imported items')}</div>
              <div className="mt-auto pt-1.5 text-2xl font-semibold leading-none text-foreground">{metrics.totalItems}</div>
            </div>
            <div className="flex min-h-[84px] flex-col rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5">
              <div className="text-sm font-medium text-foreground">{t('integrations.etsy.matching.metrics.pendingVariation', 'Pending variation')}</div>
              <div className="mt-auto pt-1.5 text-2xl font-semibold leading-none text-foreground">{metrics.variationPending}</div>
            </div>
            <div className="flex min-h-[84px] flex-col rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5">
              <div className="text-sm font-medium text-foreground">{t('integrations.etsy.matching.metrics.pendingPaperMappings', 'Pending paper mappings')}</div>
              <div className="mt-auto pt-1.5 text-2xl font-semibold leading-none text-foreground">{metrics.pendingPaperMappings}</div>
            </div>
            <div className="flex min-h-[84px] flex-col rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5">
              <div className="text-sm font-medium text-foreground">{t('integrations.etsy.matching.metrics.pendingSizeMappings', 'Pending size mappings')}</div>
              <div className="mt-auto pt-1.5 text-2xl font-semibold leading-none text-foreground">{metrics.pendingSizeMappings}</div>
            </div>
            <div className="flex min-h-[84px] flex-col rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5">
              <div className="text-sm font-medium text-foreground">{t('integrations.etsy.matching.metrics.pendingAddressMappings', 'Pending address mappings')}</div>
              <div className="mt-auto pt-1.5 text-2xl font-semibold leading-none text-foreground">{metrics.pendingAddressMappings}</div>
            </div>
            <div className="flex min-h-[84px] flex-col rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5">
              <div className="text-sm font-medium text-foreground">{t('integrations.etsy.matching.metrics.pendingImageUploads', 'Pending image uploads')}</div>
              <div className="mt-auto pt-1.5 text-2xl font-semibold leading-none text-foreground">{metrics.pendingImageUploads}</div>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card className="min-h-0">
        <CardHeader className="gap-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle>
                {activeTab === MATCHING_TABS.variations
                  ? t('integrations.etsy.matching.sections.variationKeysTitle', 'Variation keys')
                  : activeTab === MATCHING_TABS.papers
                    ? t('integrations.etsy.matching.sections.paperMatchingTitle', 'Paper matching')
                    : activeTab === MATCHING_TABS.sizes
                      ? t('integrations.etsy.matching.sections.sizeMatchingTitle', 'Size matching')
                      : activeTab === MATCHING_TABS.addresses
                        ? t('integrations.etsy.matching.sections.addressMatchingTitle', 'Address matching')
                        : t('integrations.etsy.matching.sections.imageReplacementTitle', 'Image replacement')}
              </CardTitle>
              <CardDescription>
                {activeTab === MATCHING_TABS.variations
                  ? t('integrations.etsy.matching.sections.variationKeysDescription', 'Classify shop-specific Etsy variation keys such as "Size" or "Paper and Mat". Grouped values help AI understand the key, but Papers and Sizes only consume values from approved keys.')
                  : activeTab === MATCHING_TABS.papers
                    ? t('integrations.etsy.matching.sections.paperMatchingDescription', 'Each Etsy item is reviewed separately. AI and saved rules fill similar unreviewed items automatically, but confirmation stays item-level.')
                    : activeTab === MATCHING_TABS.sizes
                      ? t('integrations.etsy.matching.sections.sizeMatchingDescription', 'Only variations classified as size should drive normalized dimensions.')
                      : activeTab === MATCHING_TABS.addresses
                        ? t('integrations.etsy.matching.sections.addressMatchingDescription', 'Review each receipt-level Etsy shipping address, complete the production draft, and confirm it for the production flow.')
                        : t('integrations.etsy.matching.sections.imageReplacementDescription', 'This tab will manage full-scale production assets for imported Etsy items, including uploads and future file-driven bulk imports.')}
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {activeTab === MATCHING_TABS.images && uploadedImageSelectorItem ? (
                <Button
                  variant="secondary"
                  onClick={() => closeUploadedImageSelector()}
                  className="gap-2"
                >
                  <Images className="h-4 w-4" />
                  {t('integrations.etsy.matching.showTable', 'Back to table view')}
                </Button>
              ) : activeTab === MATCHING_TABS.images && autoLinkedImageCount > 0 && (
                <Button
                  variant="outline"
                  onClick={handleConfirmAllImageMatches}
                  disabled={isConfirmingAllImages || isMassMatching}
                  className="gap-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 border-emerald-200"
                >
                  {isConfirmingAllImages ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  {t('integrations.etsy.matching.confirmAllSuggested', 'Confirm all suggested ({{count}})', { count: autoLinkedImageCount })}
                </Button>
              )}
              {activeTab === MATCHING_TABS.images && !uploadedImageSelectorItem && (
                <Button
                  variant={isMassMatching ? 'secondary' : 'default'}
                  onClick={() => setIsMassMatching(!isMassMatching)}
                  className="gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  {isMassMatching ? t('integrations.etsy.matching.showTable', 'Back to table view') : t('integrations.etsy.matching.showAiVault', 'Mass image matching')}
                </Button>
              )}
              <div className="relative w-full max-w-md">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t('integrations.etsy.matching.searchPlaceholder', 'Search current matching data')}
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {activeTab === MATCHING_TABS.images && isMassMatching ? (
            <EtsyMassMatchTab orders={orders} onCompleted={() => void loadData({ silent: true })} />
          ) : status === 'loading' ? (
            <div className="flex min-h-[220px] items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('integrations.etsy.matching.loading', 'Loading Etsy matching data...')}
            </div>
          ) : error ? (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              {error}
            </div>
          ) : activeTab === MATCHING_TABS.variations ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('integrations.etsy.matching.columns.variationKey', 'Variation key')}</TableHead>
                    <TableHead>{t('integrations.etsy.matching.columns.classification', 'Classification')}</TableHead>
                    <TableHead>{t('integrations.etsy.matching.columns.groupedValues', 'Grouped values')}</TableHead>
                    <TableHead>{t('integrations.etsy.matching.columns.aiDetails', 'AI details')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVariationMappings.map((mapping) => (
                    <TableRow
                      key={mapping.id}
                      className={isVariationMappingReviewPending(mapping) ? 'bg-amber-50/60 hover:bg-amber-50/80 dark:bg-amber-950/20 dark:hover:bg-amber-950/30' : undefined}
                    >
                      <TableCell className="align-top">
                        <div className="space-y-2">
                          <div className="font-medium text-foreground">{mapping.variationName}</div>
                          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                            <Badge variant="outline" className={getMatchingTagClassName('neutral')}>
                              {mapping.etsyShop.shopName}
                            </Badge>
                            <Badge variant="outline" className={getMatchingTagClassName('neutral')}>
                              {t('integrations.etsy.matching.valueCount', '{{count}} values', { count: mapping.valueCount })}
                            </Badge>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="align-top">
                        {(() => {
                          const display = getVariationDisplayState(mapping, t);

                          return (
                            <div className="space-y-2">
                              <Badge
                                variant="outline"
                                className={getMatchingTagClassName(getVariationClassificationTagTone(mapping))}
                              >
                                {display.label}
                              </Badge>
                              <Badge
                                variant="outline"
                                className={getMatchingTagClassName(isVariationMappingReviewPending(mapping) ? 'warning' : 'success')}
                              >
                                {isVariationMappingReviewPending(mapping)
                                  ? t('integrations.etsy.matching.review.needsReview', 'Needs review')
                                  : mapping.reviewStatus === 'IGNORED'
                                    ? t('integrations.etsy.matching.variation.ignore', 'Ignore')
                                    : t('integrations.etsy.matching.review.confirmed', 'Confirmed')}
                              </Badge>
                              <div className="text-xs text-muted-foreground">
                                {t('integrations.etsy.matching.classificationSource', 'Source: {{value}}', { value: mapping.classificationSource ?? t('integrations.etsy.matching.notSet', 'not set') })}
                              </div>
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="align-top">
                        <div className="flex min-w-[260px] flex-wrap gap-2">
                          {mapping.sampleValues.map((value) => (
                            <Badge
                              key={`${mapping.id}-${value}`}
                              variant="outline"
                              className={getMatchingTagClassName('neutral')}
                            >
                              {value}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="align-top">
                        {(() => {
                          const display = getVariationDisplayState(mapping, t);

                          return (
                            <div className="space-y-2 text-sm text-muted-foreground">
                              <div>{t('integrations.etsy.matching.confidence', 'Confidence: {{value}}', { value: mapping.classificationConfidence != null ? mapping.classificationConfidence.toFixed(2) : t('integrations.etsy.common.notAvailable', 'n/a') })}</div>
                              <div>{mapping.classificationReason ?? display.description}</div>
                              <div className="text-xs">{t('integrations.etsy.matching.updatedAt', 'Updated {{value}}', { value: formatDateTime(mapping.updatedAt) ?? t('integrations.etsy.common.never', 'Never') })}</div>
                              <div className="text-xs">
                                {display.description}
                              </div>
                              <div className="flex min-w-[240px] flex-col gap-2">
                                <Select
                                  value={mapping.classificationSource ? mapping.classification : undefined}
                                  onValueChange={(value: 'SIZE' | 'PAPER' | 'UNKNOWN') => {
                                    void handleSetVariationType(mapping.id, value);
                                  }}
                                  disabled={savingVariationTypeId === mapping.id || confirmingVariationTypeId === mapping.id}
                                >
                                  <SelectTrigger className={mapping.classificationSource == null ? 'border-amber-300 focus:ring-amber-300 dark:border-amber-700' : undefined}>
                                    <SelectValue placeholder={t('integrations.etsy.matching.setVariationType', 'Set variation type')} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="PAPER">{t('integrations.etsy.matching.types.paper', 'Paper')}</SelectItem>
                                    <SelectItem value="SIZE">{t('integrations.etsy.matching.types.size', 'Size')}</SelectItem>
                                    <SelectItem value="UNKNOWN">{t('integrations.etsy.matching.types.ignore', 'Ignore / Other')}</SelectItem>
                                  </SelectContent>
                                </Select>
                                {mapping.classificationSource && isVariationMappingReviewPending(mapping) ? (
                                  <Button
                                    type="button"
                                    onClick={() => void handleConfirmVariationType(mapping.id)}
                                    disabled={confirmingVariationTypeId === mapping.id || savingVariationTypeId === mapping.id}
                                  >
                                    {confirmingVariationTypeId === mapping.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                                    {t('common.confirm', 'Confirm')}
                                  </Button>
                                ) : mapping.classificationSource == null ? (
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => void handleClassifySingle(mapping.id)}
                                    disabled={classifyingMappingId === mapping.id || savingVariationTypeId === mapping.id}
                                  >
                                    {classifyingMappingId === mapping.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                                    {t('integrations.etsy.matching.askAi', 'Ask AI')}
                                  </Button>
                                ) : null}
                              </div>
                            </div>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : activeTab === MATCHING_TABS.papers ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('integrations.etsy.matching.columns.preview', 'Preview')}</TableHead>
                    <TableHead>{t('integrations.etsy.matching.columns.etsyItem', 'Etsy item')}</TableHead>
                    <TableHead>{t('integrations.etsy.matching.columns.paperGroup', 'Paper group')}</TableHead>
                    <TableHead>{t('integrations.etsy.matching.columns.itemReview', 'Item review')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupedPaperRows.flatMap((group) => {
                    return [
                      <TableRow key={`${group.key}-header`} className="bg-muted/20 hover:bg-muted/20">
                        <TableCell colSpan={4}>
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="outline" className={getMatchingTagClassName('neutral')}>
                              {group.shopName}
                            </Badge>
                            <span className="text-sm font-medium text-foreground">{group.label}</span>
                            <span className="text-xs text-muted-foreground">{t('integrations.etsy.matching.itemsCount', '{{count}} items', { count: group.items.length })}</span>
                          </div>
                        </TableCell>
                      </TableRow>,
                      ...group.items.map((row) => {
                        const primaryPaperSignal = row.primaryPaperSignal;
                        const savedRule = primaryPaperSignal
                          ? paperRules.find((rule) => (
                            rule.etsyShopId === primaryPaperSignal.etsyShopId
                            && rule.sourceKey === buildEtsyVariationMappingKey(primaryPaperSignal.variationName, row.primaryPaperValueMapping?.variationValue ?? '')
                          ))
                          : null;
                        const selectedPaperId = selectedPaperByItemId[row.itemId]
                          ?? row.matchedPaperType?.id
                          ?? savedRule?.paperType?.id
                          ?? row.primaryPaperValueMapping?.paperType?.id
                          ?? '';
                        const needsReview = Boolean(row.matchedPaperType?.id) && row.paperReviewStatus !== 'CONFIRMED';

                        return (
                          <TableRow
                            key={row.itemId}
                            className={needsReview ? 'bg-amber-50/60 hover:bg-amber-50/80 dark:bg-amber-950/20 dark:hover:bg-amber-950/30' : undefined}
                          >
                            <TableCell className="align-top">
                              <div className="flex min-w-[88px] justify-center">
                                <img
                                  src={getPreferredEtsyItemPreviewUrl(row) || noImageWatercolor}
                                  alt={row.title}
                                  className="h-20 w-20 rounded-md border border-border object-cover"
                                  loading="lazy"
                                />
                              </div>
                            </TableCell>
                            <TableCell className="align-top">
                              <div className="space-y-2">
                                <div className="font-medium text-foreground">{row.title}</div>
                                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                                  <Badge variant="outline" className={getMatchingTagClassName('neutral')}>
                                    {t('integrations.etsy.matching.receiptLabel', 'Receipt {{value}}', { value: row.etsyReceiptId })}
                                  </Badge>
                                  <Badge variant="outline" className={getMatchingTagClassName('neutral')}>
                                    {t('integrations.etsy.matching.txnLabel', 'Txn {{value}}', { value: row.transactionId })}
                                  </Badge>
                                  <Badge variant="outline" className={getMatchingTagClassName('neutral')}>
                                    {t('integrations.etsy.common.qty', 'Qty {{count}}', { count: row.quantity })}
                                  </Badge>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="align-top">
                              {primaryPaperSignal ? (
                                <div className="space-y-2 text-sm text-muted-foreground">
                                  <div className="font-medium text-foreground">
                                    {primaryPaperSignal.variationName}: {row.primaryPaperValueMapping?.variationValue ?? 'Unknown value'}
                                  </div>
                                  <div>{primaryPaperSignal.classificationReason ?? t('integrations.etsy.matching.paperAiFallback', 'AI classified this Etsy variation key as paper/material.')}</div>
                                </div>
                              ) : (
                                <div className="text-sm text-muted-foreground">{t('integrations.etsy.matching.paperNeedsVariationReview', 'Run variation mapping first so this item enters a paper group.')}</div>
                              )}
                            </TableCell>
                            <TableCell className="align-top">
                              <div className="flex min-w-[320px] flex-col gap-2">
                                <div className="flex flex-wrap gap-2">
                                  <Badge
                                    variant="outline"
                                    className={getMatchingTagClassName(row.paperReviewStatus === 'CONFIRMED' ? 'success' : 'warning')}
                                  >
                                    {row.paperReviewStatus === 'CONFIRMED'
                                      ? t('integrations.etsy.matching.review.confirmed', 'Confirmed')
                                      : row.matchedPaperType
                                        ? t('integrations.etsy.matching.review.needsReview', 'Needs review')
                                        : t('integrations.etsy.matching.review.unfilled', 'Unfilled')}
                                  </Badge>
                                  {row.paperAssignmentSource ? (
                                    <Badge
                                      variant="outline"
                                      className={getMatchingTagClassName(getAssignmentSourceTagTone(row.paperAssignmentSource))}
                                    >
                                      {row.paperAssignmentSource}
                                    </Badge>
                                  ) : null}
                                </div>
                                {row.primaryPaperValueMapping?.paperType ? (
                                  <div className="text-xs text-muted-foreground">
                                    <TooltipProvider delayDuration={150}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button
                                            type="button"
                                            className="inline-flex items-center gap-1 text-left underline decoration-dotted underline-offset-4"
                                          >
                                            <span>{t('integrations.etsy.matching.aiSuggested', 'AI suggested:')}</span>
                                            <span className="text-foreground">{getPaperLabel(row.primaryPaperValueMapping.paperType, t)}</span>
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-xs whitespace-normal">
                                          {row.primaryPaperValueMapping.paperMatchReason ?? t('integrations.etsy.matching.aiSuggestedReasonMissing', 'AI suggestion reasoning is not available yet.')}
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </div>
                                ) : null}
                                {savedRule?.paperType ? (
                                  <div className="text-xs text-muted-foreground">
                                    {t('integrations.etsy.matching.savedRule', 'Saved rule')}: {getPaperLabel(savedRule.paperType, t)}
                                  </div>
                                ) : null}
                                {row.paperAssignedAt ? (
                                  <div className="text-xs text-muted-foreground">
                                    {t('integrations.etsy.matching.updatedAt', 'Updated {{value}}', { value: formatDateTime(row.paperAssignedAt) ?? t('integrations.etsy.common.never', 'Never') })}
                                  </div>
                                ) : null}
                                <div className="flex flex-col gap-2">
                                  <Select
                                    value={selectedPaperId || undefined}
                                    onValueChange={(value) => {
                                      setSelectedPaperByItemId((current) => ({
                                        ...current,
                                        [row.itemId]: value,
                                      }));
                                      void handleSaveItemPaper(row, value);
                                    }}
                                    disabled={savingItemId === row.itemId}
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder={t('integrations.etsy.matching.selectProductionPaper', 'Select production paper')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {paperTypes.map((paper) => (
                                        <SelectItem key={paper.id} value={paper.id}>
                                          {getPaperLabel(paper, t)}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <div className="flex justify-end">
                                    <Button
                                      type="button"
                                      onClick={() => void handleConfirmItemPaper(row)}
                                      disabled={!row.matchedPaperType?.id || row.paperReviewStatus === 'CONFIRMED' || confirmingItemId === row.itemId}
                                    >
                                      {confirmingItemId === row.itemId ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                                      {t('common.confirm', 'Confirm')}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      }),
                    ];
                  })}
                </TableBody>
              </Table>
            </div>
          ) : activeTab === MATCHING_TABS.sizes ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('integrations.etsy.matching.columns.preview', 'Preview')}</TableHead>
                    <TableHead>{t('integrations.etsy.matching.columns.etsyItem', 'Etsy item')}</TableHead>
                    <TableHead>{t('integrations.etsy.matching.columns.sizeSignal', 'Size signal')}</TableHead>
                    <TableHead className="text-right">{t('integrations.etsy.matching.columns.normalizedDimensions', 'Normalized dimensions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSizeRows.map((row) => {
                    const primarySizeSignal = row.primarySizeSignal;
                    return (
                      <TableRow key={row.itemId}>
                        <TableCell className="align-top">
                          <div className="flex min-w-[88px] justify-center">
                            <img
                              src={getPreferredEtsyItemPreviewUrl(row) || noImageWatercolor}
                              alt={row.title}
                              className="h-20 w-20 rounded-md border border-border object-cover"
                              loading="lazy"
                            />
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="space-y-2">
                            <div className="font-medium text-foreground">{row.title}</div>
                            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                              <Badge variant="outline" className={getMatchingTagClassName('neutral')}>
                                {row.shopName}
                              </Badge>
                              <Badge variant="outline" className={getMatchingTagClassName('neutral')}>
                                Receipt {row.etsyReceiptId}
                              </Badge>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          {primarySizeSignal ? (
                            <div className="space-y-2 text-sm text-muted-foreground">
                              <div className="font-medium text-foreground">
                                {primarySizeSignal.variationName}: {row.primarySizeValueMapping?.variationValue ?? row.sizeLabel ?? 'Unknown value'}
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm text-muted-foreground">
                              {t('integrations.etsy.matching.rawSizeSignal', 'Raw size signal')}: {row.sizeLabel ?? t('integrations.etsy.matching.notDetectedYet', 'Not detected yet')}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="flex min-w-[320px] flex-col items-end gap-2 text-right">
                            <div className="flex flex-wrap justify-end gap-2">
                              {row.sizeAssignmentSource ? (
                                <Badge
                                  variant="outline"
                                  className={getMatchingTagClassName(getAssignmentSourceTagTone(row.sizeAssignmentSource))}
                                >
                                  {row.sizeAssignmentSource}
                                </Badge>
                              ) : null}
                            </div>
                            {typeof row.matchedWidthIn === 'number' && typeof row.matchedHeightIn === 'number' ? (
                              <div className="text-sm font-medium text-foreground">
                                {row.matchedWidthIn}" x {row.matchedHeightIn}"
                              </div>
                            ) : row.primarySizeValueMapping?.normalizedWidthIn && row.primarySizeValueMapping?.normalizedHeightIn ? (
                              <div className="text-sm font-medium text-foreground">
                                {row.primarySizeValueMapping.normalizedWidthIn}" x {row.primarySizeValueMapping.normalizedHeightIn}"
                              </div>
                            ) : (
                              <div className="text-sm text-muted-foreground">{t('integrations.etsy.matching.noNormalizedDimensionsYet', 'No normalized dimensions yet.')}</div>
                            )}
                            {editingSizeItemId === row.itemId ? (
                              <>
                                <div className="grid w-full max-w-[280px] grid-cols-2 gap-2">
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={draftSizeByItemId[row.itemId]?.widthIn ?? ''}
                                    onChange={(event) => handleSizeDraftChange(row, 'widthIn', event.target.value)}
                                    placeholder={t('integrations.etsy.matching.widthIn', 'Width in')}
                                  />
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={draftSizeByItemId[row.itemId]?.heightIn ?? ''}
                                    onChange={(event) => handleSizeDraftChange(row, 'heightIn', event.target.value)}
                                    placeholder={t('integrations.etsy.matching.heightIn', 'Height in')}
                                  />
                                </div>
                                <div className="flex justify-end gap-2">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => handleCancelSizeEdit(row.itemId)}
                                    disabled={savingSizeItemId === row.itemId}
                                  >
                                    {t('common.cancel', 'Cancel')}
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => void handleSaveItemSize(row)}
                                    disabled={savingSizeItemId === row.itemId}
                                  >
                                    {savingSizeItemId === row.itemId ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                    {t('integrations.etsy.matching.setSize', 'Set size')}
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <div className="flex justify-end">
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => handleStartSizeEdit(row)}
                                >
                                  {t('integrations.etsy.matching.changeSize', 'Change size')}
                                </Button>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : activeTab === MATCHING_TABS.addresses ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('integrations.etsy.matching.columns.preview', 'Preview')}</TableHead>
                    <TableHead>{t('integrations.etsy.matching.columns.receipt', 'Receipt')}</TableHead>
                    <TableHead>{t('integrations.etsy.matching.columns.etsySource', 'Etsy source')}</TableHead>
                    <TableHead>{t('integrations.etsy.matching.columns.productionShippingDraft', 'Production shipping draft')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAddressRows.map((order) => {
                    const draft = addressDraftByOrderId[order.id] ?? buildAddressDraftFromOrder(order);
                    const errors = validateAddressDraft(draft, t);
                    const extendedErrors = errors as Record<string, string | undefined>;
                    const hasErrors = hasAddressDraftErrors(errors);
                    const isDirty = !isDraftEqualToMatchedAddress(order, draft);
                    const isConfirmed = order.addressReviewStatus === 'CONFIRMED' && !isDirty;
                    const autocompleteSection = `section-etsy-${order.id}`;
                    const requiresNovaposhtaCitySelection = hasNovapostPresence(draft.countryCode);
                    const citySuggestions = addressCitySuggestionsByOrderId[order.id] ?? [];
                    const isCitySuggestionsOpen = addressCitySuggestionsOpenOrderId === order.id;
                    const isCitySuggestionsLoading = loadingAddressCitySuggestionsOrderId === order.id;
                    const isValidatingCity = validatingAddressCityOrderId === order.id;
                    const hasStructuredEtsyAddress = hasEtsyShipmentAddress(order);
                    const rawShipmentAddressText = rawShipmentAddressByOrderId[order.id] ?? order.rawShipmentAddressText ?? '';
                    const isParsingRawAddress = parsingRawAddressOrderId === order.id;

                    return (
                      <TableRow
                        key={order.id}
                        className={!isConfirmed && (order.matchedAddress || isDirty) ? 'bg-amber-50/60 hover:bg-amber-50/80 dark:bg-amber-950/20 dark:hover:bg-amber-950/30' : undefined}
                      >
                        <TableCell className="align-top">
                          <EtsyOrderPreviewThumbnail order={order} />
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="space-y-2">
                            <div className="font-medium text-foreground">{t('integrations.etsy.matching.receiptLabel', 'Receipt {{value}}', { value: order.etsyReceiptId })}</div>
                            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                              <Badge variant="outline" className={getMatchingTagClassName('neutral')}>
                                {order.shopName}
                              </Badge>
                              <Badge variant="outline" className={getMatchingTagClassName('neutral')}>
                                {t('integrations.etsy.matching.itemsCount', '{{count}} items', { count: order.items.length })}
                              </Badge>
                              <Badge
                                variant="outline"
                                className={getMatchingTagClassName(isConfirmed ? 'success' : 'warning')}
                              >
                                {isConfirmed
                                  ? t('integrations.etsy.matching.review.confirmed', 'Confirmed')
                                  : isDirty && order.matchedAddress
                                    ? t('integrations.etsy.matching.review.changed', 'Changed')
                                    : order.matchedAddress
                                      ? t('integrations.etsy.matching.review.needsReview', 'Needs review')
                                      : hasErrors
                                        ? t('integrations.etsy.matching.review.incomplete', 'Incomplete')
                                        : t('integrations.etsy.matching.review.draftReady', 'Draft ready')}
                              </Badge>
                            </div>
                            {order.addressAssignedAt ? (
                              <div className="text-xs text-muted-foreground">
                                {t('integrations.etsy.matching.savedAt', 'Saved {{value}}', { value: formatDateTime(order.addressAssignedAt) ?? t('integrations.etsy.common.never', 'Never') })}
                              </div>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="space-y-3 text-sm">
                            <div>
                              <div className="font-medium text-foreground">{order.etsyData.name || t('integrations.etsy.common.unknownBuyer', 'Unknown buyer')}</div>
                              <div className="text-muted-foreground">{order.etsyData.buyer_email || t('integrations.etsy.common.noEmail', 'No email')}</div>
                            </div>
                            {hasStructuredEtsyAddress ? (
                              <div className="rounded-md border border-dashed border-border/70 p-3">
                                <div className="font-medium text-foreground">
                                  {order.etsyData.formatted_address || t('integrations.etsy.matching.noEtsyAddressText', 'No Etsy address text')}
                                </div>
                                <div className="mt-2 text-muted-foreground">
                                  {formatAddressBlock(
                                    order.etsyData.first_line,
                                    order.etsyData.second_line,
                                    order.etsyData.city,
                                    order.etsyData.state,
                                    order.etsyData.zip,
                                    order.etsyData.country_iso,
                                  ) || t('integrations.etsy.matching.noStructuredDestination', 'No structured Etsy destination data')}
                                </div>
                              </div>
                            ) : (
                              <div className="rounded-md border border-dashed border-amber-200 bg-amber-50/50 p-3 text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-100">
                                <div className="text-sm font-medium">
                                  {t('integrations.etsy.matching.etsyAddressUnavailableTitle', 'Etsy shipment address is not available through API')}
                                </div>
                                <div className="mt-1 text-xs text-amber-900/80 dark:text-amber-100/80">
                                  {t('integrations.etsy.matching.etsyAddressUnavailableDescription', 'According to Etsy internal policies, Etsy may not return shipment address fields to connected apps. Copy the full shipment address from Etsy and paste it here.')}
                                </div>
                                <textarea
                                  value={rawShipmentAddressText}
                                  onChange={(event) => handleRawShipmentAddressChange(order.id, event.target.value)}
                                  placeholder={t('integrations.etsy.matching.rawShipmentAddressPlaceholder', 'Please copy the whole shipment address from Etsy as one text and paste it inside this field.')}
                                  rows={6}
                                  className="mt-3 min-h-28 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                />
                                <div className="mt-2 flex items-center gap-2 text-xs text-amber-900/80 dark:text-amber-100/80">
                                  {isParsingRawAddress ? (
                                    <>
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      {t('integrations.etsy.matching.parsingShipmentAddress', 'Parsing pasted address with AI...')}
                                    </>
                                  ) : (
                                    t('integrations.etsy.matching.rawShipmentAddressHelp', 'TulasiHome waits 2 seconds after editing, then fills high-confidence production address fields.')
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="flex min-w-[440px] flex-col gap-3">
                            <div className="grid gap-2 md:grid-cols-2">
                              <Input
                                value={draft.firstName}
                                onChange={(event) => handleAddressDraftChange(order.id, 'firstName', event.target.value)}
                                placeholder={t('integrations.etsy.matching.placeholders.firstName', 'First name')}
                                name={`etsy-address-${order.id}-firstName`}
                                autoComplete={`${autocompleteSection} given-name`}
                                className={getAddressFieldClassName(Boolean(errors.firstName))}
                              />
                              <Input
                                value={draft.lastName}
                                onChange={(event) => handleAddressDraftChange(order.id, 'lastName', event.target.value)}
                                placeholder={t('integrations.etsy.matching.placeholders.lastName', 'Last name')}
                                name={`etsy-address-${order.id}-lastName`}
                                autoComplete={`${autocompleteSection} family-name`}
                                className={getAddressFieldClassName(Boolean(errors.lastName))}
                              />
                              <Input
                                value={draft.phone}
                                onChange={(event) => handleAddressDraftChange(order.id, 'phone', event.target.value)}
                                placeholder={t('integrations.etsy.matching.placeholders.phone', 'Phone')}
                                name={`etsy-address-${order.id}-phone`}
                                autoComplete={`${autocompleteSection} tel`}
                                className={getAddressFieldClassName(Boolean(errors.phone))}
                              />
                              <Input
                                value={draft.countryCode}
                                onChange={(event) => {
                                  handleAddressDraftChange(order.id, 'countryCode', event.target.value);
                                  handleAddressDraftChange(order.id, 'novaposhtaCityRef', '');
                                  setAddressCitySuggestionsByOrderId((current) => ({ ...current, [order.id]: [] }));
                                  setAddressCitySuggestionsOpenOrderId((current) => (current === order.id ? null : current));
                                }}
                                placeholder={t('integrations.etsy.matching.placeholders.countryCode', 'Country code')}
                                name={`etsy-address-${order.id}-countryCode`}
                                autoComplete={`${autocompleteSection} country`}
                                className={getAddressFieldClassName(Boolean(errors.countryCode))}
                              />
                              <div className="md:col-span-2">
                                <Input
                                  value={draft.street1}
                                  onChange={(event) => handleAddressDraftChange(order.id, 'street1', event.target.value)}
                                  placeholder={t('integrations.etsy.matching.placeholders.street', 'Street')}
                                  name={`etsy-address-${order.id}-street1`}
                                  autoComplete={`${autocompleteSection} address-line1`}
                                  className={getAddressFieldClassName(Boolean(errors.street1) || Boolean(extendedErrors.poBoxStreet))}
                                />
                              </div>
                              <div className="md:col-span-2">
                                <Input
                                  value={draft.street2 ?? ''}
                                  onChange={(event) => handleAddressDraftChange(order.id, 'street2', event.target.value)}
                                  placeholder={t('integrations.etsy.matching.placeholders.street2', 'Apartment, suite, building')}
                                  name={`etsy-address-${order.id}-street2`}
                                  autoComplete={`${autocompleteSection} address-line2`}
                                  className={getAddressFieldClassName(false)}
                                />
                              </div>
                              <div className="relative">
                                <Input
                                  value={draft.city}
                                  onChange={(event) => {
                                    void handleAddressCityInputChange(order.id, draft.countryCode, event.target.value);
                                  }}
                                  onFocus={() => {
                                    if (requiresNovaposhtaCitySelection && citySuggestions.length > 0) {
                                      setAddressCitySuggestionsOpenOrderId(order.id);
                                    }
                                  }}
                                  placeholder={t('integrations.etsy.matching.placeholders.city', 'City')}
                                  name={`etsy-address-${order.id}-city`}
                                  autoComplete={`${autocompleteSection} address-level2`}
                                  className={getAddressFieldClassName(Boolean(errors.city) || Boolean(extendedErrors.novaposhtaCitySelection))}
                                />
                                {requiresNovaposhtaCitySelection && isCitySuggestionsOpen && (citySuggestions.length > 0 || isCitySuggestionsLoading) ? (
                                  <div className="absolute z-20 mt-2 max-h-56 w-full overflow-auto rounded-md border border-border bg-background shadow-lg">
                                    {isCitySuggestionsLoading ? (
                                      <div className="px-3 py-2 text-sm text-muted-foreground">
                                        {t('integrations.etsy.matching.loadingCities', 'Loading cities...')}
                                      </div>
                                    ) : citySuggestions.map((suggestion) => (
                                      <button
                                        key={suggestion.id}
                                        type="button"
                                        className="block w-full px-3 py-2 text-left text-sm text-foreground hover:bg-muted"
                                        onClick={() => handleAddressCitySuggestionSelect(order.id, suggestion)}
                                      >
                                        {suggestion.fullName ?? suggestion.label}
                                      </button>
                                    ))}
                                  </div>
                                ) : null}
                              </div>
                              <Input
                                value={draft.region}
                                onChange={(event) => handleAddressDraftChange(order.id, 'region', event.target.value)}
                                placeholder={t('integrations.etsy.matching.placeholders.region', 'Region / State')}
                                name={`etsy-address-${order.id}-region`}
                                autoComplete={`${autocompleteSection} address-level1`}
                                className={getAddressFieldClassName(Boolean(errors.region))}
                              />
                              <Input
                                value={draft.postalCode}
                                onChange={(event) => handleAddressDraftChange(order.id, 'postalCode', event.target.value)}
                                placeholder={t('integrations.etsy.matching.placeholders.postalCode', 'Postal code')}
                                name={`etsy-address-${order.id}-postalCode`}
                                autoComplete={`${autocompleteSection} postal-code`}
                                className={getAddressFieldClassName(Boolean(errors.postalCode))}
                              />
                            </div>
                            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                              {hasErrors ? (
                                Object.values(errors).map((message) => (
                                  <Badge
                                    key={`${order.id}-${message}`}
                                    variant="outline"
                                    className={getMatchingTagClassName('warning')}
                                  >
                                    {message}
                                  </Badge>
                                ))
                              ) : (
                                <span>{t('integrations.etsy.matching.readyForValidation', 'Ready for TulasiHome validation.')}</span>
                              )}
                            </div>
                            <div className="flex flex-wrap justify-end gap-2">
                              <Button
                                type="button"
                                variant="ghost"
                                onClick={() => {
                                  if (hasStructuredEtsyAddress) {
                                    handleResetAddressDraft(order);
                                    return;
                                  }

                                  handleParseRawShipmentAddressNow(order.id);
                                }}
                                disabled={savingAddressOrderId === order.id || confirmingAddressOrderId === order.id || isValidatingCity || isParsingRawAddress}
                              >
                                {hasStructuredEtsyAddress
                                  ? t('integrations.etsy.matching.useEtsyValues', 'Use Etsy values')
                                  : t('integrations.etsy.matching.parseWithAi', 'Parse with AI')}
                              </Button>
                              <Button
                                type="button"
                                onClick={() => void handleConfirmOrderAddress(order)}
                                disabled={isConfirmed || confirmingAddressOrderId === order.id || isValidatingCity || isParsingRawAddress}
                              >
                                {confirmingAddressOrderId === order.id || savingAddressOrderId === order.id || isValidatingCity || isParsingRawAddress ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                                {t('integrations.etsy.matching.confirmAddress', 'Confirm address')}
                              </Button>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : uploadedImageSelectorItem ? (
            uploadedImageSelectorPanel
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('integrations.etsy.matching.columns.etsyItem', 'Etsy item')}</TableHead>
                    <TableHead>{t('integrations.etsy.matching.columns.draftImage', 'Draft image')}</TableHead>
                    <TableHead>{t('integrations.etsy.matching.columns.fullSizeImage', 'Full-size image')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredImageRows.map(({ order, item }) => {
                    const hasFullSizeImage = Boolean(item.fullSizeUploadedFile);
                    const isAutoLinked = item.fullSizeImageSource === 'REUSED';
                    const imageMeta = formatImageDimensions(item.fullSizeUploadedFile);
                    const imageSize = formatFileSize(item.fullSizeUploadedFile?.sizeBytes);
                    const expectedPrintSize = formatExpectedPrintSize(item.matchedWidthIn, item.matchedHeightIn);
                    const realPrintSize = formatRealPrintSizeAt300Ppi(item.fullSizeUploadedFile);
                    const previewWidthIn = resolvePreviewPrintDimensionIn(
                      item.matchedWidthIn,
                      item.fullSizeUploadedFile?.originalWidthPx,
                    );
                    const previewHeightIn = resolvePreviewPrintDimensionIn(
                      item.matchedHeightIn,
                      item.fullSizeUploadedFile?.originalHeightPx,
                    );

                    return (
                      <TableRow
                        key={item.id}
                        className={isAutoLinked ? 'bg-amber-50/60 hover:bg-amber-50/80 dark:bg-amber-950/20 dark:hover:bg-amber-950/30' : undefined}
                      >
                        <TableCell className="align-top">
                          <div className="space-y-2">
                            <div className="font-medium text-foreground">{item.title}</div>
                            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                              <Badge variant="outline" className={getMatchingTagClassName('neutral')}>
                                {order.shopName}
                              </Badge>
                              <Badge variant="outline" className={getMatchingTagClassName('neutral')}>
                                {t('integrations.etsy.matching.receiptLabel', 'Receipt {{value}}', { value: order.etsyReceiptId })}
                              </Badge>
                              <Badge variant="outline" className={getMatchingTagClassName('neutral')}>
                                {t('integrations.etsy.matching.txnLabel', 'Txn {{value}}', { value: item.transactionId })}
                              </Badge>
                              <Badge variant="outline" className={getMatchingTagClassName('neutral')}>
                                {t('integrations.etsy.common.qty', 'Qty {{count}}', { count: item.quantity })}
                              </Badge>
                              {item.listingImageId ? (
                                <Badge variant="outline" className={getMatchingTagClassName('neutral')}>
                                  {t('integrations.etsy.matching.imageLabel', 'Image {{value}}', { value: item.listingImageId })}
                                </Badge>
                              ) : null}
                            </div>
                            {item.sku ? (
                              <div className="text-xs text-muted-foreground">{t('integrations.etsy.matching.skuLabel', 'SKU: {{value}}', { value: item.sku })}</div>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="flex min-w-[220px] flex-col gap-3">
                            {item.previewUrl ? (
                              <img
                                src={item.previewUrl}
                                alt={item.title}
                                className="h-28 w-28 rounded-md border border-border object-cover"
                              />
                            ) : (
                              <div className="flex h-28 w-28 items-center justify-center rounded-md border border-dashed border-border text-xs text-muted-foreground">
                                {t('integrations.etsy.matching.noDraftPreview', 'No draft preview')}
                              </div>
                            )}
                            <div className="space-y-1 text-xs text-muted-foreground">
                              {expectedPrintSize ? (
                                <div>
                                  <span className="font-medium text-blue-700">{t('integrations.etsy.matching.massMatch.expectedSize', 'Expected')}:</span>{' '}
                                  {expectedPrintSize}
                                </div>
                              ) : null}
                              <Badge
                                variant="outline"
                                className={getMatchingTagClassName(item.imageAvailability.status === 'ready' ? 'success' : 'warning')}
                              >
                                {t('integrations.etsy.matching.draftStatus', 'Etsy draft {{status}}', { status: item.imageAvailability.status })}
                              </Badge>
                              <div>{item.imageAvailability.note}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="flex min-w-[320px] flex-col gap-3">
                            <div className="space-y-1">
                              {item.fullSizePreviewUrl ? (
                                <button
                                  type="button"
                                  className="relative h-28 w-28 overflow-hidden rounded-md border border-border text-left transition hover:border-primary/60"
                                  onClick={() => {
                                    if (!item.fullSizeUploadedFile) {
                                      return;
                                    }
                                    setPreviewMeta({
                                      widthIn: previewWidthIn,
                                      heightIn: previewHeightIn,
                                      marginIn: null,
                                      originalWidthPx: item.fullSizeUploadedFile.originalWidthPx,
                                      originalHeightPx: item.fullSizeUploadedFile.originalHeightPx,
                                    });
                                    void handleOpenUploadedFile(item.fullSizeUploadedFile.id);
                                  }}
                                >
                                  <img
                                    src={item.fullSizePreviewUrl}
                                    alt={`${item.title} full size`}
                                    className="h-full w-full object-cover"
                                  />
                                  <span className="absolute bottom-1 right-1 rounded bg-black/65 px-1.5 py-0.5 text-[10px] font-medium text-white">
                                    {t('integrations.etsy.matching.preview', 'Preview')}
                                  </span>
                                </button>
                              ) : (
                                <div className="flex h-28 w-28 items-center justify-center rounded-md border border-dashed border-border text-xs text-muted-foreground">
                                  {t('integrations.etsy.matching.noFullSizeFile', 'No full-size file')}
                                </div>
                              )}
                              {realPrintSize ? (
                                <div className="text-xs text-muted-foreground">
                                  <span className="font-medium text-emerald-700">{t('integrations.etsy.matching.massMatch.realSizeAt300Dpi', 'Real at 300 PPI')}:</span>{' '}
                                  {realPrintSize}
                                </div>
                              ) : null}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Badge
                                variant="outline"
                                className={getMatchingTagClassName(hasFullSizeImage ? 'success' : 'warning')}
                              >
                                {hasFullSizeImage
                                  ? t('integrations.etsy.matching.uploaded', 'Uploaded')
                                  : t('integrations.etsy.matching.missing', 'Missing')}
                              </Badge>
                              {item.fullSizeImageSource ? (
                                <Badge
                                  variant="outline"
                                  className={getMatchingTagClassName(getAssignmentSourceTagTone(item.fullSizeImageSource))}
                                >
                                  {item.fullSizeImageSource === 'REUSED'
                                    ? t('integrations.etsy.matching.autoLinked', 'Auto-linked')
                                    : t('integrations.etsy.matching.manual', 'Manual')}
                                </Badge>
                              ) : null}
                            </div>
                            {item.fullSizeUploadedFile ? (
                              <div className="space-y-1 text-xs text-muted-foreground">
                                <div className="font-medium text-foreground">{item.fullSizeUploadedFile.filename}</div>
                                {imageMeta ? <div>{imageMeta}</div> : null}
                                {imageSize ? <div>{imageSize}</div> : null}
                                {item.fullSizeImageAssignedAt ? (
                                  <div>{t('integrations.etsy.matching.updatedAt', 'Updated {{value}}', { value: formatDateTime(item.fullSizeImageAssignedAt) ?? t('integrations.etsy.common.never', 'Never') })}</div>
                                ) : null}
                              </div>
                            ) : (
                              <div className="text-xs text-muted-foreground">
                                {t('integrations.etsy.matching.fullSizeImageHelp', 'Upload the production full-size image. Matching will reuse it for other unfilled Etsy items with the same source image.')}
                              </div>
                            )}
                            <input
                              ref={(node) => {
                                imageInputRefs.current[item.id] = node;
                              }}
                              type="file"
                              accept=".jpg,.jpeg,.png,.gif,.tif,.tiff,.psd,image/jpeg,image/png,image/gif,image/tiff,image/vnd.adobe.photoshop"
                              className="hidden"
                              onChange={(event) => {
                                const selectedFile = event.target.files?.[0] ?? null;
                                void handleUploadItemImage(item, selectedFile);
                              }}
                            />
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => imageInputRefs.current[item.id]?.click()}
                                  disabled={uploadingImageItemId === item.id || confirmingItemId === item.id}
                                >
                                  {uploadingImageItemId === item.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-2 h-4 w-4" />}
                                  {hasFullSizeImage
                                    ? t('integrations.etsy.matching.replaceFullSizeImage', 'Replace full-size image')
                                    : t('integrations.etsy.matching.uploadFullSizeImage', 'Upload full-size image')}
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => openUploadedImageSelector(item)}
                                  disabled={uploadingImageItemId === item.id || confirmingItemId === item.id}
                                >
                                  <Images className="mr-2 h-4 w-4" />
                                  {t('integrations.etsy.matching.useUploadedImages', 'Use uploaded images')}
                                </Button>

                                {isAutoLinked && (
                                  <Button
                                    type="button"
                                    onClick={() => void handleConfirmMatch(item.id)}
                                    disabled={confirmingItemId === item.id || uploadingImageItemId === item.id}
                                  >
                                    {confirmingItemId === item.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                                    {t('integrations.etsy.matching.confirmMatch', 'Confirm match')}
                                  </Button>
                                )}
                              </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      {previewImageUrl ? (
        <ImageZoomModal
          isOpen={isPreviewOpen}
          onClose={closePreview}
          imageUrl={previewImageUrl}
          widthIn={previewMeta?.widthIn ?? null}
          heightIn={previewMeta?.heightIn ?? null}
          marginIn={previewMeta?.marginIn ?? null}
          originalWidthPx={previewMeta?.originalWidthPx ?? null}
          originalHeightPx={previewMeta?.originalHeightPx ?? null}
          showDownload={false}
        />
      ) : null}
    </div>
  );
};

export default EtsyMatchingCanvas;
